/* Personalized-edition builder (Wonderbly-style), phase 1 + the Sept 2026 flow (v2).
   Ported from "20 Shopify Mock/v2/builder.js". Needs:
     - #pb-config (JSON from sections/personalized-builder.liquid): prices, labels, asset URLs, variant id,
       consent text/version, support email
     - window.BOOK_PAGES (assets/personalized-book-data.js)
   Flow: child + your email -> photos (the photo-permission box first, then each photo cropped in the browser) -> free preview -> pick the one of two
   drawings that looks more like the child -> the cover reveal -> make it a gift (dedication + from) -> check the book -> cart.
   Every email links back with ?preview=<id>, which reopens the builder at the preview's step (proxy mode only).
   One retry, hidden behind a quiet "See photo tips" link under the revealed cover; after it, an email address.
   Everything "AI" goes through window.PersonalizeAPI below. While the section's "Preview demo" box is ticked that is a
   client-side demo (timed progress + the Calvin sample images, files pb-boaz-*); otherwise the app proxy at /apps/personalize.
   Preview images are watermarked by the server; this file never adds a mark of its own.
   The cart step is REAL: it posts to /cart/add.js with line-item properties.
   Debug hooks (demo only): #s=start|preview|child|photos|crop|generating|pick|revealed|tips|retry|limit|gift|ready */
(function () {
  "use strict";
  const cfgEl = document.getElementById("pb-config");
  if (!cfgEl) return;
  let CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { console.error("[pb] bad #pb-config", e); return; }
  if (CFG.book && window.BOOK_MODEL?.id !== CFG.book) { console.error("[pb] missing book data", CFG.book); return; }
  // A missing book keeps every Apes default. Book-specific behavior is declared by its generated data.
  const MODEL = CFG.book && window.BOOK_MODEL && window.BOOK_MODEL.id === CFG.book ? window.BOOK_MODEL : {};
  const DEMO_BOOK = MODEL.demo || {};
  const ORDERING_OPEN = MODEL.ordering?.supported !== false && CFG.orderingOpen !== false;
  const copy = (key, fallback) => CFG.copy?.[key] ?? MODEL.copy?.[key] ?? fallback;
  /* Babies (age 0 = "Under 1", newborn to 11 months): only a book whose data says allow_baby offers them (Apes, from its generated
     personalized-book-data.js; a named book from its model). The edge refuses age 0 for any other book. */
  const ALLOW_BABY = CFG.book ? MODEL.allow_baby === true : window.BOOK_ALLOW_BABY === true;
  const BABY_LABEL = "Under 1";
  const AGES = [...(ALLOW_BABY ? [0] : []), 1, 2, 3, 4, 5, 6, 7, 8, "9+"];
  const ageLabel = a => (String(a) === "0" ? BABY_LABEL : String(a));
  /* A status carries the stored age, a number: "9+" is kept as 9 (the edge reads "9+" as 9). Restored, 9 or more is the
     "9+" choice again, so the age button, the check step and the cart line say "9+", never "9". */
  const restoredAge = a => (a == null || a === "" ? "" : /^\d+$/.test(String(a)) && +a >= 9 ? "9+" : String(a));
  const ageRadios = () => AGES.map(a => (a === 0 ? radio(0, BABY_LABEL, ' class="bk-baby"') : radio(a, a))).join("");
  const U = k => {
    const example = DEMO_BOOK.examples?.[st.gender];
    const key = DEMO_BOOK.asset_aliases?.[k]?.replace("{example}", example) || (example && k.startsWith("boaz-") ? `${example}-${k.slice(5)}` : k);
    return (CFG.assets && (CFG.assets[key] || CFG.assets[k])) || "";
  };

  /* =====================================================================================
     PersonalizeAPI: the only place that talks to the "AI".

     Contract v2 (Sept 2026). DemoAPI and ProxyAPI implement the same shapes; the builder relies on nothing else.
     Boxes are [x0, y0, x1, y1], normalized 0..1, of the EXIF-oriented image, origin top-left.

       checkPhoto(jpeg, info)        POST /apps/personalize/photos/check
             multipart: photo = the WHOLE photo, downscaled to ~1600 px on the long side, JPEG
         ->  { ok: boolean, reason?: string,
               faces: [{ box: [x0,y0,x1,y1], score: 0..1 }],
               suggested_crop: [x0,y0,x1,y1] }
             (info = { width, height, srcWidth, srcHeight } is only read by the demo)

       createPreview({ child, photos })   POST /apps/personalize/previews
             multipart: child    = JSON { name, gender: "boy"|"girl", age, eyes,
                                          email, consent: true, consent_version, device_id }
                        photos[] = the buyer's CROPS, 1..4, JPEG q 0.9, <= 3000 px on the long side
         ->  { preview_id, status: "queued" }

       getStatus(preview_id)         GET  /apps/personalize/previews/:id
         ->  { preview_id, status: "queued"|"running"|"awaiting_pick"|"ready"|"failed", progress: 0..100, stage: 0|1|2,
               sheets: [{ sheet: 1|2, url }] (awaiting_pick and after), picked_sheet, portrait_url?, cover_url?, cover_cart_url? (a 90-day signed
               link to the watermarked cover, null until it exists: the cart line's hidden "_cover_image"),
               child: { name, gender, age, eyes }, attempt, retries_left, error? }        (images arrive watermarked)

       pick(preview_id, sheet, attempt)   POST /apps/personalize/previews/:id/pick   JSON { sheet: 1|2, attempt }
             attempt = the status payload's attempt (the run the customer was shown)
         ->  the status shape (status "running": the cover is being painted); poll getStatus again
             409 not_awaiting_pick: the preview moved on (a stale attempt, or already picked elsewhere): reload the status.
             503 busy (with Retry-After) at the daily cap: nothing changed; the customer can pick again later.

       retry(preview_id, photos)     POST /apps/personalize/previews/:id/retry
             multipart: photos[] = new crops, as above; device_id (the edge keeps the one stored at create);
                        consent=true + consent_version (the photo step's box; the edge records the new version)
         ->  { preview_id, status: "queued", retries_left }    then poll getStatus(preview_id) again
             Only ONE retry per preview. After that the server answers non-2xx with JSON { error: "retry_limit" }.

     A "failed" preview is finished for good (error_code "no_usable_photo" = needs_photo, with photo_feedback
     [{ photo: n, accepted, problems: [text] }]; anything else, or an expired preview, is our fault). The builder then
     starts a NEW preview (POST /previews) from the photo step; it never spends the customer's one retry on it.

     Every email links back with ?preview=<id>; the builder reopens at the step the preview is at. The old
     POST /previews/:id/approve and /pages answer 410 { error: "gone" }.
     Errors: non-2xx bodies are JSON { error: "<code>", message: "..." }; they throw an Error with .status and
     .code (= body.error). retry_limit is HTTP 409; the per-email/device/IP preview limits on create are HTTP 429
     rate_limited_email | rate_limited_device | rate_limited_ip. At the daily preview-spend cap create and retry answer
     HTTP 503 busy (with Retry-After): nothing was made or used up; the builder says so and never re-posts by itself.
     (The status payload may also carry approved: true; it is ignored.)
     ===================================================================================== */
  const API_BASE = "/apps/personalize";

  const uuid = () => (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => { const r = Math.random() * 16 | 0; return (c === "x" ? r : (r & 3 | 8)).toString(16); });
  const wait = ms => new Promise(r => setTimeout(r, ms));
  const apiError = (status, code) => Object.assign(new Error(code || "personalize API " + status), { status, code: code || null });
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  /* A centered square-ish box, used whenever there's no usable suggestion. */
  function centeredBox(W, H) {
    const s = 0.7 * Math.min(W, H), x = (W - s) / 2, y = (H - s) / 2;
    return [x / W, y / H, (x + s) / W, (y + s) / H];
  }
  /* Head-and-shoulders crop around one face box (used when the buyer taps a face marker). */
  function cropAroundFace(b, W, H, others = []) {
    const fx = b[0] * W, fy = b[1] * H, fw = (b[2] - b[0]) * W, fh = (b[3] - b[1]) * H;
    // clip at the photo's edges rather than sliding the box inward, which would pull a neighbour's face in
    const cx = fx + fw / 2;
    let x0 = Math.max(0, cx - fw * 1.1) / W, x1 = Math.min(W, cx + fw * 1.1) / W;
    let y0 = Math.max(0, fy - fh * 0.4) / H, y1 = Math.min(H, fy - fh * 0.4 + fh * 2.6) / H;
    // then stop short of any other face that would fall inside
    others.forEach(o => {
      if (o === b || o[2] <= x0 || o[0] >= x1 || o[3] <= y0 || o[1] >= y1) return;
      if (o[1] >= b[3]) y1 = Math.min(y1, o[1]); else if (o[3] <= b[1]) y0 = Math.max(y0, o[3]);
      else if (o[0] >= b[2]) x1 = Math.min(x1, o[0]); else if (o[2] <= b[0]) x0 = Math.max(x0, o[2]);
    });
    return [x0, y0, x1, y1];
  }

  /* Demo: stored face boxes + suggested crops for the Calvin sample photos (pb-boaz-*), keyed by their oriented pixel size
     (the four theme assets, plus the 752x1208 original of pb-boaz-photo1). Anything else gets a centered box and no faces. */
  const BOAZ1 = { faces: [[0.20, 0.13, 0.62, 0.39], [0.00, 0.62, 0.26, 0.93]], crop: [0.06, 0.04, 0.82, 0.58] };
  const DEMO_SAMPLES = {
    "560x900": BOAZ1, "752x1208": BOAZ1,
    "743x900": { faces: [[0.21, 0.10, 0.55, 0.40]], crop: [0.08, 0.02, 0.68, 0.55] },
    "585x900": { faces: [[0.32, 0.10, 0.68, 0.37]], crop: [0.14, 0.03, 0.86, 0.55] },
    "600x900": { faces: [[0.30, 0.10, 0.72, 0.37]], crop: [0.16, 0.03, 0.86, 0.55] },
  };

  const DemoAPI = {
    demo: true,
    _jobs: {},
    async checkPhoto(jpeg, info = {}) {
      await wait(900);
      const s = DEMO_SAMPLES[`${info.srcWidth}x${info.srcHeight}`];
      if (s) return { ok: true, faces: s.faces.map((box, i) => ({ box, score: i ? 0.82 : 0.97 })), suggested_crop: s.crop };
      return { ok: true, faces: [], suggested_crop: centeredBox(info.width || 1, info.height || 1) };
    },
    async createPreview({ child, photos }) {
      const id = "demo-" + uuid();
      this._jobs[id] = { t0: Date.now(), ms: 5200, child, n: (photos || []).length, retries: 1, attempt: 1, picked: null, t1: 0 };
      await wait(120);
      return { preview_id: id, status: "queued" };
    },
    async getStatus(id) {
      const j = this._jobs[id];
      if (!j) return { preview_id: id, status: "failed", progress: 0, stage: 0, error: "unknown preview" };
      const child = { name: j.child.name, gender: j.child.gender, age: parseInt(j.child.age, 10), eyes: j.child.eyes };   // a number, as the edge stores it ("9+" is 9)
      if (!j.picked) {
        const progress = Math.min(100, Math.round(((Date.now() - j.t0) / j.ms) * 100));
        if (progress < 100) return { preview_id: id, status: "running", progress, stage: progress < 20 ? 0 : 1, child, attempt: j.attempt, retries_left: j.retries };
        const artwork = DEMO_BOOK.compose ? await demoBookArt(j.child.gender) : null;
        return { preview_id: id, status: "awaiting_pick", progress: 100, stage: 1, child, attempt: j.attempt, retries_left: j.retries,
                 sheets: artwork && DEMO_BOOK.pick_pages ? DEMO_BOOK.pick_pages.map((id, i) => ({ sheet: i + 1, url: artwork[id] })) : [1, 2].map(n => ({ sheet: n, url: U("boaz-sheet" + n) || U("boaz-portrait") })) };
      }
      const artwork = DEMO_BOOK.compose ? await demoBookArt(j.child.gender) : null;
      const progress = Math.min(100, Math.round(((Date.now() - j.t1) / j.ms) * 100)), done = progress >= 100;
      return { preview_id: id, status: done ? "ready" : "running", progress, stage: 2, picked_sheet: j.picked, child, attempt: j.attempt,
               portrait_url: done ? U("boaz-portrait") : null, cover_url: done ? (artwork?.cover || U("boaz-cover")) : null,
               ...(artwork && done ? { book_art: artwork } : {}),
               cover_cart_url: done ? new URL(U("boaz-cover"), location.href).href : null, retries_left: j.retries };
    },
    async pick(id, sheet, attempt) {
      await wait(150);
      const j = this._jobs[id];
      if (!j) throw apiError(404, "not_found");
      if (j.picked || attempt !== j.attempt) throw apiError(409, "not_awaiting_pick");
      j.picked = sheet; j.t1 = Date.now();
      return this.getStatus(id);
    },
    async retry(id, photos) {
      await wait(200);
      const j = this._jobs[id];
      if (/(^|[#&])limit=1\b/.test(location.hash)) throw apiError(409, "retry_limit");    // demo: force the limit (#s=retry&limit=1)
      if (!j) throw apiError(404, "not_found");
      if (j.retries < 1) throw apiError(409, "retry_limit");
      j.retries--; j.t0 = Date.now(); j.n = (photos || []).length;     // same Calvin images come back; that's fine for the demo
      j.picked = null; j.attempt++;
      return { preview_id: id, status: "queued", attempt: j.attempt, retries_left: j.retries };
    },
  };

  /* The app-proxy implementation. NOT used while the section's "Preview demo" box is ticked. */
  const ProxyAPI = {
    demo: false,
    async _json(r) {
      let body = null;
      try { body = await r.json(); } catch (e) {}
      if (!r.ok) throw apiError(r.status, body && body.error);
      return body;
    },
    _post(path, body, headers) { return fetch(API_BASE + path, { method: "POST", body, headers, credentials: "same-origin" }).then(r => this._json(r)); },
    checkPhoto(jpeg) {
      const fd = new FormData(); fd.append("photo", jpeg, "photo.jpg");
      return this._post("/photos/check", fd);
    },
    createPreview({ child, photos }) {
      const fd = new FormData(); fd.append("child", JSON.stringify(child));
      photos.forEach((b, i) => fd.append("photos[]", b, `photo${i + 1}.jpg`));
      return this._post("/previews", fd);
    },
    async getStatus(id) { return this._json(await fetch(`${API_BASE}/previews/${encodeURIComponent(id)}`, { credentials: "same-origin" })); },
    retry(id, photos, deviceId, consentVersion) {
      const fd = new FormData();
      if (deviceId) fd.append("device_id", deviceId);
      if (consentVersion) { fd.append("consent", "true"); fd.append("consent_version", consentVersion); }
      photos.forEach((b, i) => fd.append("photos[]", b, `photo${i + 1}.jpg`));
      return this._post(`/previews/${encodeURIComponent(id)}/retry`, fd);
    },
    pick(id, sheet, attempt) {
      return this._post(`/previews/${encodeURIComponent(id)}/pick`, JSON.stringify({ sheet, attempt }), { "Content-Type": "application/json" });
    },
  };
  const PersonalizeAPI = CFG.demo === false && !DEMO_BOOK.only ? ProxyAPI : DemoAPI;
  window.PersonalizeAPI = PersonalizeAPI;
  const POLL_MS = PersonalizeAPI.demo ? 110 : 1500;

  /* =====================================================================================
     Builder
     ===================================================================================== */
  const PAGES = Object.fromEntries((window.BOOK_PAGES || []).map(p => [p.id, p]));
  // the price checkout charges (the variant's price; CFG.price is its compare-at when there is one): the bar shows it beside
  // the button at every step, on its own: never a struck-through sale, never in the button's label, and never a coupon
  // code (the code stays on the landing, away from the buy button)
  const PRICE = +CFG.sale || +CFG.price || 45;
  const TITLE = CFG.bookTitle || MODEL.titleTemplate || "I Did Not Come From Apes", AUTHOR = CFG.author ?? "Christopher Fenison";
  // TODO (legal): CONSENT is placeholder copy (section setting "consent_text"); have counsel review before launch.
  const CONSENT = (CFG.consentText || "I confirm I have the right to use these photos.").replace(/\{name\}/gi, "{n}");
  const RETENTION = CFG.retentionNote || "We keep {n}'s preview for 90 days so you can come back to it, then delete it.";
  const EMAIL_HINT = CFG.emailHint || "We'll email you the preview and a few reminders about it.";
  const CONSENT_VERSION = CFG.consentVersion || "2026-09-v3";
  const MAIL = CFG.supportEmail || "hello@pediment.com";
  const CROP_MAX = 3000, CHECK_MAX = 1600;     // px, long side: the crop we upload / the copy we send to photos/check
  const MIN_SIDE = 400, FACE_WARN = 300;       // px of the original: block below / warn below
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const esc = s => String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const el = h => { const t = document.createElement("template"); t.innerHTML = h.trim(); return t.content.firstElementChild; };
  // line icons (stroke = currentColor) in place of emoji
  const ICON = {
    pen: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/>',
    camera: '<path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/>',
    lock: '<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
    gift: '<path d="M20 12v9H4v-9"/><path d="M2 7h20v5H2z"/><path d="M12 21V7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>',
    bag: '<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>',
    eye: '<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/>',
    check: '<path d="M20 6L9 17l-5-5"/>',
  };
  const icon = (k, s = 16) => `<svg class="bk-ic" width="${s}" height="${s}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICON[k]}</svg>`;
  const phone = () => matchMedia("(max-width: 700px)").matches;
  const money = n => "$" + (n % 1 ? n.toFixed(2) : n);
  const money2 = n => "$" + Number(n).toFixed(2);
  const [TT1, TT2] = (() => { const w = TITLE.split(" "); const k = Math.ceil(w.length / 2) + 1; return [w.slice(0, k).join(" "), w.slice(k).join(" ")]; })();

  // ---------------------------------------------------------------- motion (Oct 2026: one continuous move per step change)
  /* A step change: the outgoing step slides out briefly (140 ms), the box's height follows the new content (280 ms), the
     new step's blocks slide in one after another (280 ms each, 40 ms apart), forward to the left and Back to the right.
     transform/opacity only (the individual translate property, so no element's own transform is overwritten), plus the
     height tween. The header, the questions' cover and progress bar, and the book behind a step stay where they are.
     prefers-reduced-motion: reduce (or no Web Animations): every swap is instant and nothing scrolls smoothly. */
  const RM = window.matchMedia ? matchMedia("(prefers-reduced-motion: reduce)") : { matches: false };
  const calm = () => RM.matches || !Element.prototype.animate;
  const EASE_OUT = "cubic-bezier(.22,.8,.24,1)", EASE_IN = "cubic-bezier(.4,0,.9,.55)";
  const SHIFT = 18, T_OUT = 140, T_IN = 280, T_H = 280, STAGGER = 40;
  const smooth = () => (calm() ? "instant" : "smooth");
  /* nodes slide in one after another: dir 1 = forward (from the right), -1 = back (from the left); axis "y" rises */
  function slideIn(nodes, dir = 1, { delay = 0, step = STAGGER, dist = SHIFT, dur = T_IN, axis = "x", max = 6 } = {}) {
    if (calm()) return;
    nodes.filter(n => n && n.isConnected && !n.hidden).forEach((n, i) => n.animate(
      [{ opacity: 0, translate: axis === "y" ? `0 ${dist}px` : `${dir * dist}px 0` }, { opacity: 1, translate: "0 0" }],
      { duration: dur, delay: delay + Math.min(i, max) * step, easing: EASE_OUT, fill: "backwards" }));
  }
  /* the box grows or shrinks from the height it had (from) to its new content's height, clipped meanwhile */
  function tweenHeight(box, from, dur = T_H) {
    if (box._hA) box._hA.cancel();
    if (calm() || !box.isConnected) return;
    const to = box.getBoundingClientRect().height;
    if (!from || Math.abs(to - from) < 2) return;
    if (box._ov == null) box._ov = box.style.overflow;
    box.style.overflow = "hidden";
    const a = box._hA = box.animate([{ height: from + "px" }, { height: to + "px" }], { duration: dur, easing: EASE_OUT });
    a.onfinish = a.oncancel = () => { if (box._hA !== a) return; box._hA = null; box.style.overflow = box._ov; box._ov = null; };
  }
  /* focus the new step's heading (a programmatic target only: tabindex -1, no outline, no scroll jump) */
  function focusHeading(h) {
    if (!h || !h.isConnected) return;
    if (!h.hasAttribute("tabindex")) h.tabIndex = -1;
    try { h.focus({ preventScroll: true }); } catch (e) { h.focus(); }
  }

  // ---------------------------------------------------------------- state
  const st = {
    name: "", gender: "boy", age: "", eyes: "", email: "", consent: false, childDone: false,
    genderPicked: false,   // the boy/girl question shows no answer until one is tapped (pron() keeps "boy" for the cover meanwhile)
    photos: [],            // cropped photos: {uid, file (original Blob), w, h, view (URL of the ~1600px copy), check, checking,
                           //                  crop [x0,y0,x1,y1], blob (cropped JPEG), thumb (URL), small}
    retryMode: false, sentSig: "", sentPhotos: null,
    photoFeedback: null,   // needs_photo: the server's per-photo verdicts for the photos we sent (shown in the photo step)
    previewId: null, portraitUrl: null, pending: null,
    creating: false,       // POST /previews is on its way (before st.pending exists): the child's details are already locked
    revealed: false, attempt: 0, retriesLeft: null,
    art: {},               // page id -> personalized image url (from the API)
    dedication: "", from: "", giftDone: false,   // the "Make it a gift" step (dedication + sign-off line, both optional)
    view: null,
  };
  if (PersonalizeAPI.demo) window.__pbState = st;      // review tests only (the harness); never in proxy mode
  const pron = () => (st.gender === "girl" ? { she: "she", her: "her", Her: "Her", him: "her" } : { she: "he", her: "his", Her: "His", him: "him" });
  const nm = () => st.name || "the child";
  const ded = () => st.dedication;
  function fill(t, q = x => x) {
    const p = pron();
    return String(t).replace(/\{DED\}/g, () => q(ded())).replace(/\{FROM\}/g, () => q(st.from))
      .replace(/\{NU\}/g, () => q((st.name || "NAME").toUpperCase())).replace(/\{N\}/g, () => q(st.name || "The child"))
      .replace(/\{n\}/g, () => q(nm())).replace(/\{she\}/g, p.she).replace(/\{her\}/g, p.her).replace(/\{Her\}/g, p.Her).replace(/\{him\}/g, p.him).replace(/\{He\}/g, st.gender === "girl" ? "She" : "He").replace(/\{himself\}/g, st.gender === "girl" ? "herself" : "himself");
  }
  const emailOk = () => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(st.email);
  const sig = () => st.photos.map(p => p.uid).join(",");
  const retryAvailable = () => st.revealed && st.attempt < 2 && st.retriesLeft !== 0;
  // the print takes the child's details from the preview, not the cart line: once a preview exists they are read-only
  const childLocked = () => !!(st.previewId || st.pending || st.revealed || st.creating);
  // short: the bottom tab's label on phones, where all the tabs share one row
  const STEPS = [
    { id: "child", label: "Child", short: "Child", done: () => st.childDone, value: () => st.name },
    { id: "photos", label: "Photos", short: "Photos", done: () => st.photos.length > 0 || st.revealed || !!st.pending, value: () => { const n = st.photos.length; return n ? `${n} photo${n > 1 ? "s" : ""}` : ""; } },
    { id: "preview", label: "See {n} in the book", short: "Preview", done: () => st.revealed, value: () => (st.revealed ? "Done" : "Free") },
    { id: "gift", label: "Make it a gift", short: "Gift", done: () => st.giftDone, value: () => (st.giftDone ? (st.dedication || st.from ? "Added" : "Skipped") : "Optional") },
  ];
  const nextStep = () => STEPS.find(s => !s.done());
  const photosReady = () => STEPS[1].done();

  // ---------------------------------------------------------------- book model
  /* window.BOOK_LAYOUT (assets/personalized-book-data.js, generated by the Pediment OS script
     backend/scripts/personalized_builder_assets.py from the pipeline's document.py) holds the printed book's text elements:
     positions in book points, fonts, sizes, leading, colours, shadows. elHTML() lays each one out the way print-render
     does (an absolutely placed box, one <p> per paragraph), sized in --pt so it scales with the page. */
  const LAYOUT = window.BOOK_LAYOUT || { page_pt: [612, 792], cover_pt: [630, 810], title: { els: [] }, cover: { els: [] }, recap: { L: { els: [] }, R: { els: [] } }, back: { els: [] } };
  const [PW, PH] = LAYOUT.page_pt, [CVW, CVH] = LAYOUT.cover_pt;
  /* the front cover as a reader sees it (spine edge to board edge: the box the printed front text is centred in): its shape
     and point scale for every cover the CSS draws (the cover sheet, the drawing box, the start screen, the cart drawer's
     thumb), set once from the data so cover_pt stays the one source. On <html>: the cart drawer sits outside #bk. */
  document.documentElement.style.setProperty("--pb-cv-ar", `${CVW} / ${CVH}`);
  document.documentElement.style.setProperty("--pb-cv-w", String(CVW));
  const SPREADS = (window.BOOK_PAGES || []).filter(p => /^p\d\d-\d\d$/.test(p.id));
  const DAY = { "p04-05": "Day 1", "p06-07": "Day 2", "p08-09": "Day 3", "p10-11": "Day 4", "p12-13": "Day 5", "p14-15": "Day 6", "p16-17": "Day 6", "p18-19": "Day 6", "p20-21": "Day 7" };
  const isReal = id => !!st.art[id];
  // a gendered plate (a boy's p30-31 is the p30-31-boy plate): its own art, geometry and text tone
  const pageData = id => { const pd = PAGES[id], v = pd && pd.variants && pd.variants[st.gender]; return v ? { ...pd, ...v, id } : pd; };
  const art = id => { const pd = pageData(id); return st.art[id] || (pd && pd.art && U(pd.art)) || U("ph-" + id); };

  // Measure in book points once: the same fit scales to HTML covers and painted thumbnails.
  // A long name can use two lines at half size; the subtitle keeps its own fixed position.
  const coverMeasure = document.createElement("canvas").getContext("2d");
  function coverTitleLayout() {
    return LAYOUT.cover.els.map((e, i) => {
      const text = fill(e.paras[0].text), width = e.w * .98;
      const measure = (line, size) => {
        coverMeasure.font = `${e.weight} ${size}px "Cabin Sketch"`;
        const m = coverMeasure.measureText(line);
        return Math.max(m.width, m.actualBoundingBoxLeft + m.actualBoundingBoxRight);
      };
      const fit = (lines, max) => {
        const widest = Math.max(...lines.map(line => measure(line, max)));
        return Math.min(max, max * width / (widest || 1));
      };
      let lines = [text], size = fit(lines, e.size);
      if (i === 0 && size < e.size * LAYOUT.cover.fit.min_ratio) {
        // Prefer a whole word or hyphen break. Keep every character, including the hyphen.
        const chars = Array.from(text), breaks = chars.map((c, j) => /[\s-]/.test(c) ? j + 1 : 0).filter(j => j > 0 && j < chars.length);
        const candidates = (breaks.length ? breaks : chars.slice(1).map((_, j) => j + 1)).map(j => [chars.slice(0, j).join("").trimEnd(), chars.slice(j).join("").trimStart()]).filter(pair => pair.every(Boolean));
        const max = Math.min(e.size, (LAYOUT.cover.els[1].y - e.y - 4) / (2 * 1.08));
        candidates.sort((a, b) => fit(b, max) - fit(a, max));
        if (candidates.length) { lines = candidates[0]; size = fit(lines, max); }
      }
      return { ...e, lines, size, lead: size * 1.08 };
    });
  }
  const coverElsHTML = () => LAYOUT.cover.els.map((e, i) => elHTML(e, CVW, CVH, { cls: "bk-cvtext", coverIndex: i })).join("");
  function fitCover() {
    const layout = coverTitleLayout();
    $$(".bk-cvtext", bk).forEach(d => {
      const e = layout[+d.dataset.coverIndex];
      d.style.fontSize = PT(e.size); d.style.lineHeight = PT(e.lead);
      d.firstElementChild.innerHTML = e.lines.map(esc).join("<br>");
    });
  }

  const demoArtCache = new Map();
  const loadArt = url => new Promise((resolve,reject) => { const img = new Image(); img.crossOrigin = "anonymous"; img.onload = () => resolve(img); img.onerror = () => reject(new Error("Could not load sample artwork")); img.src = url; });
  async function demoBookArt(gender) {
    const key=gender+":"+nm();
    if (demoArtCache.has(key)) return demoArtCache.get(key);
    const promise = (async () => {
      const who = DEMO_BOOK.examples[gender], out = {};
      await Promise.all(SPREADS.filter(p => p.hasChild).map(async p => {
        const [base,child] = await Promise.all([loadArt(U(p.art)),loadArt(U(`${who}-${p.spreadId}`))]);
        const canvas = document.createElement("canvas"); canvas.width = base.width; canvas.height = base.height;
        const ctx = canvas.getContext("2d"); ctx.drawImage(base,0,0);
        // the example child's whole page: the placeholder's gold glow reaches past the swap box, so a box paste would leave it showing
        ctx.drawImage(child,0,0,base.width,base.height);
        out[p.id] = canvas.toDataURL("image/jpeg",.94);
      }));
      const cover = await loadArt(U(`${who}-cover-clean`));
      await document.fonts.ready;
      const cv=document.createElement("canvas"); cv.width=cover.width;cv.height=cover.height;
      const cvctx=cv.getContext("2d");cvctx.drawImage(cover,0,0);cvctx.textAlign="center";cvctx.textBaseline="alphabetic";
      const scale = cv.width / CVW;
      for (const e of coverTitleLayout()) {
        cvctx.fillStyle=e.color; cvctx.font=`${e.weight} ${e.size * scale}px "Cabin Sketch"`;
        const m=cvctx.measureText(e.lines[0]), ascent=m.fontBoundingBoxAscent, descent=m.fontBoundingBoxDescent;
        const baseline=(e.y + e.lead / 2) * scale + (ascent - descent) / 2;
        e.lines.forEach((line, i) => cvctx.fillText(line, (e.x + e.w / 2) * scale, baseline + i * e.lead * scale));
      }
      out.cover=cv.toDataURL("image/jpeg",.94);
      const [base,title] = await Promise.all([loadArt(U("ph-p01")),loadArt(U(`${who}-title`))]);
      const c = document.createElement("canvas"); c.width=base.width; c.height=base.height;
      const ctx=c.getContext("2d"); ctx.drawImage(base,0,0); // Only the example's art below title_crop is copied, so example names, dedication and sign-off stay out.
      const tc = DEMO_BOOK.title_crop; ctx.drawImage(title,0,title.height*tc,title.width,title.height*(1-tc),0,base.height*tc,base.width,base.height*(1-tc)); // from title_crop down to the foot (.5: the lower half)
      out.p01=c.toDataURL("image/jpeg",.94);
      return out;
    })();
    demoArtCache.set(key,promise); return promise;
  }
  const PT = v => `calc(${+(+v).toFixed(3)} * var(--pt))`;
  const ptCss = s => String(s).replace(/(-?\d*\.?\d+)pt/g, (_, n) => PT(n));
  const FONT = { story: "var(--story)", display: "var(--sketch)", legal: "var(--legal, var(--story))" };   // legal: a book's copyright face
  function elHTML(e, W = PW, H = PH, { cls = "", style = "", coverIndex = null } = {}) {
    const s = [`left:${(e.x / W) * 100}%`, `top:${(e.y / H) * 100}%`, `width:${(e.w / W) * 100}%`, `font-family:${FONT[e.font] || FONT.story}`,
      `font-weight:${e.weight}`, `font-size:${PT(e.size)}`, `line-height:${PT(e.lead)}`, `color:${e.color}`, `text-align:${e.align}`,
      `letter-spacing:${e.ls || 0}em`];
    if (e.balance) s.push("text-wrap:balance");
    if (e.shadow) s.push(`text-shadow:${ptCss(e.shadow)}`);
    if (style) s.push(style);
    const ps = e.paras.map(p => {
      const q = [`margin:0 0 ${PT(p.gap || 0)}`];
      if (p.size) q.push(`font-size:${PT(p.size)}`);
      if (p.lead) q.push(`line-height:${PT(p.lead)}`);
      if (p.ls != null) q.push(`letter-spacing:${p.ls}em`);
      // runs: parts of one line in their own weight or colour (a book's order link); none on Apes' pages
      const runs = p.runs && p.runs.filter(r => r.text).map(r => `<span style="${r.weight ? `font-weight:${r.weight};` : ""}${r.color ? `color:${r.color}` : ""}" data-tpl="${esc(r.text)}"></span>`).join("");
      if (p.href) return `<p style="${q.join(";")}"><a href="${esc(p.href)}"${runs ? `>${runs}` : ` data-tpl="${esc(p.text)}">`}</a></p>`;
      return runs ? `<p style="${q.join(";")}">${runs}</p>` : `<p style="${q.join(";")}" data-tpl="${esc(p.text)}"></p>`;
    }).join("");
    return `<div class="bk-el${cls ? " " + cls : ""}" ${coverIndex == null ? "" : ` data-cover-index="${coverIndex}"`} style="${s.join(";")}">${ps}</div>`;
  }

  function onPage(pd, single, side = "C") {
    const b = pd.box, cx = (b[0] + b[2]) / 2, cy = b[1] + (b[3] - b[1]) * (pd.id === "p01" ? 0.62 : 0.42);
    if (single) return { side, x: cx, y: cy };
    const sd = cx < 50 ? "L" : "R";
    return { side: sd, x: sd === "L" ? cx * 2 : (cx - 50) * 2, y: cy };
  }

  function storyHTML(t) {
    if (t.el) return elHTML(t.el, PW, PH, { cls: "bk-story" });
    const [top, left, w] = t.box;     // data from before BOOK_LAYOUT: book_spec's box (top, left, width) and lines
    const lines = t.lines.map(x => `<p data-tpl="${esc(x)}"></p>`).join("") + (t.cite ? `<p class="cite">${esc(t.cite)}</p>` : "");
    return `<div class="bk-story old ${t.tone}${t.tight ? " tight" : ""}${t.display ? " display" : ""}" style="top:${top / 7.92}%;left:${left / 6.12}%;width:${w / 6.12}%">${lines}</div>`;
  }

  function frostPill(pd, single, side) {
    if (pd.hasChild === false) return "";                                   // a page without the child has no pill
    if (PersonalizeAPI.demo && DEMO_BOOK.compose && st.revealed) return "";  // the demo draws the example child into every page
    const c = onPage(pd, single, side);
    if (c.side !== side) return "";
    const x = clamp(c.x, 30, 70);      // the label may wrap to 60% of the page wide (CSS): centred here, it stays on the page
    return `<div class="bk-onpage" style="left:${x}%;top:${c.y}%"><span class="bk-frostpill" data-tpl="${st.revealed ? "{N} is drawn into these pages when you order" : "{N} goes here"}"${st.revealed ? "" : ' data-go="photos"'}></span></div>`;
  }

  function pageHTML(pd, side, single, extra = "") {
    let inner = "";
    if (pd.text) pd.text.filter(t => t.side === side).forEach(t => (inner += storyHTML(t)));
    inner += frostPill(pd, single, side);
    if (pd.id === "p08-09" && pd.name_area) {
      const a = pd.name_area, pl = side === "L" ? 0 : 50, cx = (a[0] + a[2]) / 2;
      const x0 = (a[0] - pl) * 2, x1 = (a[2] - pl) * 2, box = `top:${a[1]}%;height:${a[3] - a[1]}%`;
      if ((cx < 50 ? "L" : "R") === side) {
        inner += `<div class="bk-sand cross" style="left:${x0}%;width:${x1 - x0}%;${box}"><span data-tpl="{NU}"></span><i class="stick"></i></div>`;
        const s0 = clamp(x0, 3, 100), s1 = Math.max(x1, s0 + 40);
        inner += `<div class="bk-sand solo" style="left:${s0}%;width:${s1 - s0}%;${box}"><span data-tpl="{NU}"></span><i class="stick"></i></div>`;
        if (!childLocked()) inner += `<div class="bk-onpage" style="left:${clamp((cx - pl) * 2, 30, 70)}%;top:${a[1] - 3}%"><button type="button" class="bk-pill" data-go="child">${icon("pen")}Change name</button></div>`;
      }
    } else if (pd.name_area && !single) {
      // the name the child signs in the art (p28-29: the crayon drawing on the wall): centred on the manifest's name area
      const a = pd.name_area, cx = (a[0] + a[2]) / 2, sd = cx < 50 ? "L" : "R", pl = sd === "L" ? 0 : 50;
      if (sd === side) inner += `<div class="bk-crayon" data-w="${((a[2] - a[0]) * 2).toFixed(2)}" style="left:${((cx - pl) * 2).toFixed(2)}%;top:${((a[1] + a[3]) / 2).toFixed(2)}%"><span data-tpl="{NU}"></span></div>`;
    }
    return `<div class="bk-pg ${side}${single ? " single" : ""}" data-side="${side}" style="background-image:url('${art(pd.id)}')"><div class="bk-in">${inner}${extra}</div></div>`;
  }

  function sheet(id, kind, pagesHTML, cap, after = "", beside = "") {
    const spread = `<div class="bk-spread">${pagesHTML}</div>`;
    return `<section class="bk-sheet k-${kind}${isReal(id) ? " real" : ""}" data-id="${id}">${beside ? `<div class="bk-cvrow">${spread}${beside}</div>` : spread}
      <div class="bk-cap">${cap}</div>${after}</section>`;
  }

  function coverSheet() {
    // the printed cover text; the cover image the server returns already carries it (the backend draws it in), so once that
    // image is shown (.real outside the demo, whose sample art has no text) CSS hides these (.bk-cvtext) to avoid doubling
    const txt = (LAYOUT.cover.fit ? coverElsHTML() : LAYOUT.cover.els.map(e => elHTML(e, CVW, CVH, { cls: "bk-cvtext" })).join("")) +
      `<div class="bk-onpage only-pre" style="left:50%;top:39%"><button type="button" class="bk-pill" data-go="photos">${icon("camera")}<span data-tpl="${esc(copy("photoPill", "Add {n}'s photo"))}"></span></button></div>`;
    // after the reveal: portrait thumbnail + the quiet photo-tips link (filled by paintAfter)
    // before any photo: the cover at half the view with the next step beside it (#bk.needs-photos, set by live())
    // .bk-next-ok: the bridge from the email question ("Thanks! We'll email {n}'s preview to …"), filled by live()
    const next = `<aside class="bk-next" aria-label="Next step">
      <p class="bk-next-ok" hidden></p>
      <h2 tabindex="-1" data-tpl="Next: add photos of {n}"></h2>
      <p>${esc(copy("nextPhotos", "1–4 clear photos of their face. We'll draw them into the book and show you the cover for free."))}</p>
      <button type="button" class="bk-btn bk-next-go" data-go="photos">${icon("camera")}Add photos</button></aside>`;
    return sheet("cover", "cover", pageHTML(pageData("cover"), "C", true, txt), "Front cover", `<div class="bk-after only-post" aria-live="polite"></div>`, next);
  }
  /* Page 1 as printed (document.title_page): the title, the byline, "Made especially for {name}" (shrunk to fit, fitTitle),
     then the buyer's dedication (at most 3 lines, measured WRAP_SLACK narrower than its frame, as the print does) and
     the sign-off line, exactly as the buyer typed it, one line below the dedication's last line. */
  // the title page's printed text (also drawn, scaled down, inside the "Make it a gift" step: titlePreview)
  function titleEls() {
    const T = LAYOUT.title, slack = T.wrap_slack || 0;
    const narrow = e => ({ ...e, x: e.x + slack / 2, w: e.w - slack });
    return T.els.map((e, i) => i === T.dedication ? elHTML(narrow(e), PW, PH, { cls: "bk-tp-ded", style: `--lines:${T.dedication_lines || 3}` })
      : i === T.from ? elHTML({ ...narrow(e), paras: e.paras.map((q, j) => (j ? q : { ...q, text: "{FROM}" })) }, PW, PH, { cls: "bk-tp-from" })   // the sign-off, as typed
      : i === T.name ? elHTML(e, PW, PH, { cls: "bk-tp-name" }) : elHTML(e)).join("");
  }
  function titleSheet() {
    const txt = titleEls() + `<div class="bk-onpage" style="left:50%;top:54%"><button type="button" class="bk-pill" data-go="gift">${icon("pen")}Write a dedication</button></div>`;
    return sheet("p01", "title", `<div class="bk-pg L blank"></div>` + pageHTML(pageData("p01"), "R", true, txt), "Page 1 · Title page");
  }
  /* The same page, cropped to its text (title to the sign-off line) for the gift step: live() fills it and fitTitle() lays
     out the name, the dedication (3 lines at most) and the sign-off line exactly as on the page and in print. */
  const titlePreview = () => `<figure class="bk-gprev" aria-label="Preview of the title page"><div class="bk-pg R single" aria-hidden="true"
      style="background-image:url('${art("p01")}')"><div class="bk-in">${titleEls()}</div></div><figcaption>Page 1 · Title page</figcaption></figure>`;
  function fitTitle() {
    const T = LAYOUT.title; if (!T || T.name == null) return;
    const E = T.els[T.name], F = T.name_fit || {};
    $$(".bk-tp-name", bk).forEach(d => {
      const pg = d.closest(".bk-pg"), W = pg && pg.clientWidth, p = d.firstElementChild; if (!W || !p) return;
      const r = document.createRange(), width = () => { r.selectNodeContents(p); return r.getBoundingClientRect().width; };
      let s = E.size; d.style.fontSize = PT(s);
      while (F.min && s > F.min && width() > (F.width * W) / PW) { s -= F.step; d.style.fontSize = PT(s); }
    });
    $$(".bk-tp-ded", bk).forEach(d => {
      const pg = d.closest(".bk-pg"), W = pg && pg.clientWidth, de = T.els[T.dedication], fr = $(".bk-tp-from", pg); if (!fr) return;
      const lead = (de.lead * W) / PW, n = !st.dedication ? 0 : W ? clamp(Math.round(d.firstElementChild.getBoundingClientRect().height / lead), 1, T.dedication_lines || 3) : 1;
      fr.style.top = `${((de.y + de.lead * n) / PH) * 100}%`;
    });
  }
  function paperPage(side, P) {
    const bg = P.art && U(P.art) ? ` style="background-image:url('${U(P.art)}');background-size:100% 100%"` : "";
    return `<div class="bk-pg ${side} paper"${bg}><div class="bk-in">${P.els.map(e => elHTML(e)).join("")}</div></div>`;
  }
  function recapSheet() { return sheet("p32-33", "recap", paperPage("L", LAYOUT.recap.L) + paperPage("R", LAYOUT.recap.R), "Pages 32–33 · The Seven Days"); }
  function backSheet() { return sheet("p34", "back", paperPage("L", LAYOUT.back) + `<div class="bk-pg R blank"></div>`, LAYOUT.back.caption || "Page 34 · About the author"); }

  let builtGender = null;
  function bookHTML() {
    builtGender = st.gender;
    let h = coverSheet() + titleSheet();
    SPREADS.forEach(p => {
      const pd = pageData(p.id), [a, b] = pd.id.slice(1).split("-").map(Number);
      h += sheet(pd.id, "spread", pageHTML(pd, "L", false) + pageHTML(pd, "R", false), `Pages ${a}–${b}${pd.label ? " · " + esc(fill(pd.label)) : DAY[pd.id] ? " · " + DAY[pd.id] : ""}`);
    });
    return h + (LAYOUT.recap ? recapSheet() : "") + backSheet();
  }

  // ---------------------------------------------------------------- builder shell
  let bk, scroller, bar, mw;
  /* The book's cover title and the name in the sand are set in Cabin Sketch. Load it once if the page hasn't (the v2 landing
     doesn't), so both landings' builders draw it. */
  function loadSketchFont() {
    const has = [...document.querySelectorAll("link[href]")].some(l => /cabin(\+|%20| |-)sketch/i.test(l.href))
      || (document.fonts && [...document.fonts].some(f => /cabin.?sketch/i.test(f.family)));
    if (has) return;
    document.head.appendChild(el(`<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cabin+Sketch:wght@700&display=swap">`));
  }
  function shell() {
    loadSketchFont();
    const badgeText = CFG.demoBadge ?? "Preview demo · sample results";     // a book may set "" for no badge
    const badge = PersonalizeAPI.demo && badgeText ? `<span class="bk-mock">${esc(badgeText)}</span>` : "";
    // the server's cover (and the demo's sample cover, now cut the same way) carries the printed text: no overlay on it
    bk = el(`<div id="bk" data-book="${esc(CFG.book || "apes")}" class="bk srv-art" data-screen="start" aria-modal="true" role="dialog" aria-label="Personalize your book">
      <header class="bk-top"><div class="bk-top-t"><b>${esc(MODEL.headerTitle || TITLE)}</b><span>${esc(CFG.builderSub || "Build Your Book")}</span></div>
        <div class="bk-top-r">${badge}<button type="button" class="bk-x" data-close aria-label="Close">✕</button></div></header>
      <section class="bk-start"></section>
      <section class="bk-prev">
        <div class="bk-scroll">
          <div class="bk-head"><h1 data-tpl="Personalize your book for {N}"></h1>
            <div class="bk-view" role="group" aria-label="Page view">
              <button type="button" data-view="one"><i class="ic one"></i>One page</button><button type="button" data-view="two"><i class="ic two"></i>Two pages</button></div></div>
          <div class="bk-book"></div>
          <div class="bk-end"><p>That's the whole book: ${esc(CFG.bookPages || "34")} pages, hardcover, 8.5×11.</p></div>
        </div>
        <footer class="bk-bar"><div class="bk-chips"></div><div class="bk-go"><div class="bk-price"><b>${money(PRICE)}</b></div><button type="button" class="bk-cont"></button></div></footer>
      </section>
      <div class="bk-mw"><div class="bk-m" role="dialog"></div></div>
      <div class="bk-toast" role="status" aria-live="polite"></div>
    </div>`);
    document.body.append(bk);
    scroller = $(".bk-scroll", bk); bar = $(".bk-bar", bk); mw = $(".bk-mw", bk);
    bk.addEventListener("click", onClick);
    // the revealed cover (the server's image, text drawn in): a tap opens it full screen with a small zoom (owner: "a little")
    bk.addEventListener("click", e => {
      const pg = e.target.closest(".k-cover.real .bk-pg");
      if (!pg || e.target.closest("button, a") || !st.art.cover) return;
      sheetViewer([{ url: st.art.cover, sheet: 1 }], 0, null, pg, { max: 2.5, title: fill("{N}'s cover"), label: fill("{N}'s cover") });
    });
    mw.addEventListener("click", e => { if (e.target === mw) { if (genOpen()) minimizeGen(); else closeModal(); } });
  }

  function onClick(e) {
    const t = e.target.closest("button, [data-go], [data-close], [data-view], a[data-back]");
    if (!t || !bk.contains(t)) return;
    if (t.matches("[data-copy]")) { copyText(t); return; }          // under the cover and inside modals
    if (mw.contains(t)) return;
    if (t.matches("[data-close], a[data-back]")) { e.preventDefault(); closeBuilder(); return; }
    if (t.dataset.view) { st.view = t.dataset.view; applyView(); return; }
    if (t.matches("[data-tips]")) { openTips("reveal"); return; }
    if (t.dataset.go) { openStep(t.dataset.go === "photos" && photosReady() && !st.revealed ? "preview" : t.dataset.go); return; }
    if (t.dataset.step) { openStep(t.dataset.step); return; }
    if (t.matches(".bk-cont")) { const n = nextStep(); if (n) openStep(n.id); else stepCheck(t); }
  }

  function openBuilder(screen = "start") {
    if (!bk) shell();
    document.documentElement.classList.add("bk-lock");
    bk.classList.add("open");
    // once a preview exists the child's details are read-only, and once the questions are answered they are done:
    // either way the landing's buttons reopen the book, never question 1
    screen === "start" && !childLocked() && !st.childDone ? showStart() : showPreview();
  }
  /* Every email links back with ?preview=<id>: reopen the builder at the preview's step, on any device. */
  async function resume(id) {
    let s;
    try { s = await PersonalizeAPI.getStatus(id); }
    catch (err) {
      console.warn("[pb] resume failed", err && (err.code || err.status));
      if (err && err.status === 404) { openBuilder("start"); failModal(); }        // purged: start over from the form
      return;
    }
    if (!s || !s.child) return;
    // an expired preview keeps its row but not the child's details: then the child step comes first again
    Object.assign(st, { name: s.child.name || "", gender: s.child.gender || "boy", genderPicked: !!s.child.gender, age: restoredAge(s.child.age),
                        eyes: s.child.eyes || "", childDone: !!s.child.name, previewId: id, attempt: +s.attempt || 1,
                        retriesLeft: s.retries_left ?? null });
    openBuilder("preview");
    st.pending = { id, attempt: st.attempt, picked: s.picked_sheet || null };
    if (s.status === "ready") finishGen(s);
    else if (s.status === "awaiting_pick") stepPick(s);
    else if (s.status === "queued" || s.status === "running") stepGen(null);
    else previewFailed(s);          // failed, needs_photo or expired
  }
  function closeBuilder() { bk.classList.remove("open"); document.documentElement.classList.remove("bk-lock"); closeModal(); }

  // ---------------------------------------------------------------- start screen
  const EYES = [["Brown", "#6b4423"], ["Blue", "#4a7fc1"], ["Green", "#5a8f4e"], ["Hazel", "#8e7543"], ["Gray", "#8a949c"]];
  /* The child form (start screen and the "Who is this book for?" modal). Boy/Girl, age and eye color are radio groups
     (role=radiogroup/radio, aria-checked, arrow keys, one tab stop each). Continue always looks tappable: with fields
     missing it puts a short note under each one and moves focus to the first. */
  const radios = (id, label, items) => `<div class="bk-f" id="${id}-f"><span id="${id}-l">${label}</span><div class="${items.cls}" id="${id}" role="radiogroup" aria-labelledby="${id}-l">${items.html}</div><small class="bk-ferr" id="${id}-err" hidden></small></div>`;
  const radio = (v, inner, extra = "") => `<button type="button" role="radio" aria-checked="false" tabindex="-1" data-v="${v}"${extra}>${inner}</button>`;
  /* Radio-group behaviour shared by the child form and the question screens: paintRadios marks the checked radio and
     makes it (else the first) the group's one tab stop; radioKeys moves with the arrows / Home / End and selects. */
  function paintRadios(g, cur) {
    const bs = $$("[role=radio]", g);
    bs.forEach(b => { const on = b.dataset.v === cur; b.classList.toggle("on", on); b.setAttribute("aria-checked", String(on)); });
    const tab = bs.find(b => b.dataset.v === cur) || bs[0];
    bs.forEach(b => (b.tabIndex = b === tab ? 0 : -1));
    return tab;
  }
  function radioKeys(g, pick) {
    g.onkeydown = e => {
      const bs = $$("[role=radio]", g), i = bs.indexOf(document.activeElement); if (i < 0) return;
      const j = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: bs.length - 1 }[e.key];
      if (j == null) return;
      e.preventDefault(); const b = bs[(j + bs.length) % bs.length]; pick(b); b.focus();
    };
  }
  function childFields(prefix) {
    return `<label class="bk-f" id="${prefix}-namef"><span>First name</span><input type="text" id="${prefix}-name" maxlength="${MODEL.maxName || 14}" autocomplete="off" placeholder="The child's first name" value="${esc(st.name)}" aria-describedby="${prefix}-name-err"><small class="bk-ferr" id="${prefix}-name-err" hidden></small></label>
      ${radios(`${prefix}-g`, "This book is for a", { cls: "bk-seg", html: radio("boy", "Boy") + radio("girl", "Girl") })}
      ${radios(`${prefix}-age`, "Age", { cls: "bk-ages", html: ageRadios() })}
      ${radios(`${prefix}-eyes`, "Eye color", { cls: "bk-eyes", html: EYES.map(([n, c]) => radio(n, `<i style="background:${c}" aria-hidden="true"></i>${n}`)).join("") })}
      <div class="bk-you">
        <label class="bk-f" id="${prefix}-emailf"><span>Your email</span><input type="email" id="${prefix}-email" autocomplete="email" inputmode="email" autocapitalize="off" spellcheck="false" placeholder="you@example.com" value="${esc(st.email)}" aria-describedby="${prefix}-email-err ${prefix}-email-hint">
          <small class="bk-ferr" id="${prefix}-email-err" hidden></small>
          <small class="bk-hint" id="${prefix}-email-hint">${esc(EMAIL_HINT)}</small></label>
      </div>`;
  }
  function wireChild(root, prefix, onChange) {
    const groups = [["g", () => st.gender, v => { st.gender = v; st.genderPicked = true; live(); }], ["age", () => String(st.age), v => { st.age = v; }], ["eyes", () => st.eyes, v => { st.eyes = v; }]];
    const err = (key, text) => { const e = $(`#${prefix}-${key}-err`, root); if (!e) return; e.textContent = text || ""; e.hidden = !text; e.closest(".bk-f").classList.toggle("bad", !!text); };
    const paint = () => {
      groups.forEach(([k, get]) => paintRadios($(`#${prefix}-${k}`, root), get()));      // one tab stop per group
      onChange && onChange();
    };
    groups.forEach(([k, _get, set]) => {
      const g = $(`#${prefix}-${k}`, root);
      const pick = b => { set(b.dataset.v); err(k, ""); paint(); };
      g.onclick = e => { const b = e.target.closest("[role=radio]"); if (b) pick(b); };
      radioKeys(g, pick);                                                               // arrows move and select
    });
    const ei = $(`#${prefix}-email`, root), ni = $(`#${prefix}-name`, root);
    const emailErr = () => (!st.email ? copy("emailRequired", "Enter an email so we can send the preview") : !emailOk() ? "Please check your email address." : "");
    ni.addEventListener("input", e => { st.name = e.target.value.trim().replace(/^./, c => c.toUpperCase()); if (st.name) err("name", ""); live(); paint(); });
    ei.addEventListener("input", () => { st.email = ei.value.trim(); if ($(`#${prefix}-emailf`, root).classList.contains("bad")) err("email", emailErr()); paint(); });
    ei.addEventListener("blur", () => { if (st.email) err("email", emailErr()); });
    paint(); live();
    // Continue: true when the form is complete; otherwise a note by every missing field and focus on the first
    return () => {
      const missing = [["name", !st.name && "Enter the child's first name", ni], ["age", !st.age && "Pick an age", $(`#${prefix}-age [role=radio]`, root)],
                       ["eyes", !st.eyes && "Pick an eye color", $(`#${prefix}-eyes [role=radio]`, root)], ["email", emailErr(), ei]];
      missing.forEach(([k, msg]) => err(k, msg || ""));
      const first = missing.find(([, msg]) => msg);
      if (first) { first[2].focus(); first[2].scrollIntoView && first[2].scrollIntoView({ block: "center", behavior: smooth() }); return false; }
      return true;
    };
  }
  /* The start: one question per screen (name, boy/girl, age, eyes, email), each title carrying the child's name.
     A tap on an answer moves on; Back keeps every answer. The all-in-one stepChild() modal stays for edits. */
  const QUESTIONS = [
    { id: "name",   title: () => "Who is this book for?" },
    { id: "gender", title: () => fill("Is {N} a boy or a girl?") },
    { id: "age",    title: () => fill("How old is {N}?") },
    { id: "eyes",   title: () => fill("What color are {N}'s eyes?") },
    { id: "email",  title: () => fill(copy("emailQuestion", "Where should we send {N}'s preview?")) },
  ];
  const TAP_GUARD_MS = 250;      // a fast double tap must not answer the next question too
  function showStart() { showQuestion(0, 1, true); }
  /* The cover and the progress bar are drawn once and stay put; only .bk-qbody (the title, the answers, Back) changes.
     Moving between questions: the old body slides out (140 ms, inert meanwhile, so a second tap can't answer it), the
     body's height eases to the new one, and the new title, answers and Back slide in from the side we're heading to. */
  let qSeq = 0;
  function showQuestion(i, dir = 1, enter = false) {
    bk.dataset.screen = "start";
    const q = QUESTIONS[i], s = $(".bk-start", bk), pct = Math.round((i / QUESTIONS.length) * 100), seq = ++qSeq;
    let qb = $(".bk-qbody", s);
    const fresh = !qb;
    if (fresh) {
      s.innerHTML = `<div class="bk-start-in">
        <div class="bk-mini"><div class="bk-mini-cv" style="background-image:url('${U("ph-cover")}')">
          ${LAYOUT.cover.fit ? coverElsHTML() : `<div class="t">${esc(TT1)}<br><span>${esc(TT2)}</span></div><div class="s" data-tpl="{N} discovers how {she} was made"></div>`}</div></div>
        <div class="bk-start-form bk-q">
          <div class="bk-qbar" role="progressbar" aria-label="Progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}"><i style="width:${pct}%"></i></div>
          <div class="bk-qbody"></div>
        </div></div>`;
      qb = $(".bk-qbody", s);
    }
    const qbar = $(".bk-qbar", s);       // the same bar every time: its width eases forward (or back) to this question
    qbar.setAttribute("aria-valuenow", pct); qbar.firstElementChild.style.width = pct + "%";
    if (qb._out) { qb._out.cancel(); qb._out = null; }
    const open = bk.classList.contains("open");
    if (fresh || enter || calm() || !open || !qb.childElementCount) {
      renderQ();
      // the builder opening: the cover, then the question, rise into place (the overlay itself fades in, CSS)
      if ((fresh || enter) && open) slideIn([$(".bk-mini", s), $(".bk-start-form", s)], 1, { axis: "y", dist: 12, delay: 30, step: 70 });
      return;
    }
    const h0 = qb.getBoundingClientRect().height;
    qb.inert = true;
    if (s.scrollTop > 0) s.scrollTo({ top: 0, behavior: "smooth" });
    const out = qb._out = qb.animate([{ opacity: 1, translate: "0 0" }, { opacity: 0, translate: `${-dir * SHIFT}px 0` }], { duration: T_OUT, easing: EASE_IN, fill: "forwards" });
    out.finished.then(() => {
      if (seq !== qSeq) return;
      qb._out = null; renderQ(); out.cancel();
      tweenHeight(qb, h0);
      slideIn([...qb.children], dir, { delay: 20 });
    }, () => {});

    function renderQ() {
      qb.inert = false;
      const body = {
        name: `<input type="text" id="q-name" maxlength="${MODEL.maxName || 14}" autocomplete="off" placeholder="The child's first name" value="${esc(st.name)}" aria-label="First name">
               <small class="bk-ferr" id="q-err" hidden></small><button type="button" class="bk-btn" id="q-go">Continue</button>`,
        gender: `<div class="bk-seg big" role="radiogroup" aria-label="Boy or girl">${radio("boy", "Boy")}${radio("girl", "Girl")}</div>`,
        age: `<div class="bk-ages big" role="radiogroup" aria-label="Age">${ageRadios()}</div>`,
        eyes: `<div class="bk-eyes big" role="radiogroup" aria-label="Eye color">${EYES.map(([n, c]) => radio(n, `<i style="background:${c}" aria-hidden="true"></i>${n}`)).join("")}</div>`,
        email: `<input type="email" id="q-email" autocomplete="email" inputmode="email" autocapitalize="off" spellcheck="false" placeholder="you@example.com" value="${esc(st.email)}" aria-label="Your email">
                <small class="bk-hint">${esc(EMAIL_HINT)}</small><small class="bk-ferr" id="q-err" hidden></small>
                <button type="button" class="bk-btn" id="q-go">Continue</button>`,
      }[q.id];
      qb.innerHTML = `<h1>${esc(q.title())}</h1>${body}
          ${i ? `<button type="button" class="bk-back" id="q-back">← Back</button>` : `<a href="#" class="bk-back" data-back>← Back to the product page</a>`}`;
      const shownAt = performance.now(), early = () => performance.now() - shownAt < TAP_GUARD_MS;
      let gone = false;                                      // one move per screen: a second Enter or tap mid-transition is ignored
      const next = () => { if (gone) return; gone = true; if (i + 1 < QUESTIONS.length) showQuestion(i + 1, 1); else toPreview(); };
      const err = t => { const e = $("#q-err", s); if (e) { e.textContent = t; e.hidden = !t; } };
      const g = $("[role=radiogroup]", s);
      if (g) {
        const cur = () => ({ gender: st.genderPicked ? st.gender : "", age: String(st.age), eyes: st.eyes }[q.id]);
        const set = v => { st[q.id] = q.id === "age" ? String(v) : v; if (q.id === "gender") st.genderPicked = true; paintRadios(g, cur()); live(); };
        let going = false;
        g.onclick = e => {                                   // a tap (or Enter / Space) answers and moves on
          const b = e.target.closest("[role=radio]"); if (!b || going || early()) return;
          going = true; set(b.dataset.v); setTimeout(next, 120);
        };
        radioKeys(g, b => set(b.dataset.v));                 // arrows choose without moving on
        paintRadios(g, cur()).focus({ preventScroll: true });   // focus stays in the dialog: the checked radio, else the first
      }
      const inp = $("#q-name, #q-email", s);
      if (inp) {
        inp.addEventListener("input", () => { if (q.id === "name") { st.name = inp.value.trim().replace(/^./, c => c.toUpperCase()); live(); } else st.email = inp.value.trim(); err(""); });
        inp.addEventListener("keydown", e => { if (e.key === "Enter") $("#q-go", s).click(); });
        inp.focus({ preventScroll: true });
      }
      const go = $("#q-go", s);
      if (go) go.onclick = () => {
        if (q.id === "name" && !st.name) return err("Enter the child's first name");
        if (q.id === "email" && !emailOk()) return err(st.email ? "Please check your email address." : copy("emailRequired", "Enter an email so we can send the preview"));
        next();
      };
      const back = $("#q-back", s); if (back) back.onclick = () => { if (early() || gone) return; gone = true; showQuestion(i - 1, -1); };
      live();
    }
  }

  // ---------------------------------------------------------------- preview screen
  function showPreview() {
    bk.dataset.screen = "preview";
    if (!st.view) st.view = phone() ? "one" : "two";
    $(".bk-book", bk).innerHTML = bookHTML();
    applyView(); live();
  }
  /* The email question -> the book with the photo step (the biggest change of the flow: owner, "What just happened?").
     One move instead of a cut: the question slides out while the progress bar fills, the small cover grows into the
     book's cover (FLIP), the page title, the "Next: add photos" card (whose first line confirms where the preview goes)
     and the step bar arrive after it, focus goes to the card's heading, and on a phone, where the card sits under the
     cover, the book scrolls smoothly until the card is in view above the step bar. */
  function toPreview() {
    st.childDone = true;
    const s = $(".bk-start", bk), qb = $(".bk-qbody", s), cv = $(".bk-mini-cv", s), seq = ++qSeq;
    if (calm() || !bk.classList.contains("open") || !qb || !cv) { showPreview(); focusHeading($(".bk-next h2", bk)); revealNext(); return; }
    const qbar = $(".bk-qbar", s);
    if (qbar) { qbar.setAttribute("aria-valuenow", "100"); qbar.firstElementChild.style.width = "100%"; }
    qb.inert = true;
    const out = qb._out = qb.animate([{ opacity: 1, translate: "0 0" }, { opacity: 0, translate: `${-SHIFT}px 0` }], { duration: 200, easing: EASE_IN, fill: "forwards" });
    out.finished.then(() => {
      if (seq !== qSeq) return;
      const from = cv.getBoundingClientRect();
      qb._out = null; showPreview(); out.cancel(); qb.inert = false;
      enterPreview(from);
    }, () => {});
  }
  function enterPreview(from) {
    const cover = $('.bk-sheet[data-id="cover"] .bk-spread', bk), nx = $(".bk-next", bk), next = nx && nx.offsetParent ? nx : null;
    if (cover && from && from.width) {
      const to = cover.getBoundingClientRect();
      if (to.width && to.top < innerHeight) {
        cover.animate([{ transformOrigin: "0 0", transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width})` },
                       { transformOrigin: "0 0", transform: "none" }], { duration: 360, easing: EASE_OUT });
      }
    }
    slideIn([$(".bk-head h1", bk), $(".bk-view", bk)], 1, { axis: "y", dist: 10, delay: 90, step: 50 });
    slideIn([$('.bk-sheet[data-id="cover"] .bk-cap', bk), ...$$(".bk-sheet", bk).slice(1, 3)], 1, { axis: "y", dist: 14, delay: 260, step: 60 });
    if (next) {
      // beside the cover (desktop) it comes in from the right; under it (phone) it rises
      const stacked = getComputedStyle(nx.parentElement).flexDirection === "column";
      slideIn([nx], 1, stacked ? { axis: "y", dist: 16, delay: 150, dur: 320 } : { dist: 28, delay: 150, dur: 320 });
      slideIn([$(".bk-next-ok", nx), $("h2", nx), $("p:not(.bk-next-ok)", nx), $(".bk-next-go", nx)], 1, { axis: "y", dist: 8, delay: 230, step: 55 });
    }
    if (bar) bar.animate([{ translate: "0 100%" }, { translate: "0 0" }], { duration: 320, delay: 120, easing: EASE_OUT, fill: "backwards" });
    focusHeading(next && $("h2", next));
    setTimeout(revealNext, 560);
  }
  /* the photo step's card fully in view above the step bar (phones), never scrolled past its own top */
  function revealNext() {
    const nx = $(".bk-next", bk);
    if (!nx || !nx.offsetParent || bk.dataset.screen !== "preview" || !bk.classList.contains("open") || (mw && mw.classList.contains("open"))) return;
    const r = nx.getBoundingClientRect(), sr = scroller.getBoundingClientRect(), limit = Math.min(sr.bottom, bar ? bar.getBoundingClientRect().top : sr.bottom) - 16;
    const by = Math.min(r.bottom - limit, r.top - sr.top - 12);
    if (by > 4) scroller.scrollTo({ top: scroller.scrollTop + by, behavior: smooth() });
  }
  function applyView() {
    bk.classList.toggle("v-one", st.view === "one"); bk.classList.toggle("v-two", st.view !== "one");
    $$("[data-view]", bk).forEach(b => b.classList.toggle("on", b.dataset.view === st.view));
  }

  function live() {
    if (!bk) return;
    if (DEMO_BOOK.asset_aliases) $$(".bk-mini-cv, .bk-gen-art", bk).forEach(e => { e.style.backgroundImage = `url('${U("ph-cover")}')`; });
    // a gendered plate (p30-31) changes with the child's gender: rebuild the book (the revealed art is kept in st.art)
    if (builtGender && builtGender !== st.gender && bk.dataset.screen === "preview" && SPREADS.some(p => p.variants)) { $(".bk-book", bk).innerHTML = bookHTML(); }
    bk.classList.toggle("revealed", st.revealed);
    bk.classList.toggle("needs-photos", !photosReady() && !st.revealed);
    // the placeholder pages: before the photos an action ("{N} goes here"), after the reveal only a note (the book is drawn when ordered)
    $$(".bk-frostpill", bk).forEach(e => {
      e.dataset.tpl = st.revealed ? "{N} is drawn into these pages when you order" : "{N} goes here";
      if (st.revealed) e.removeAttribute("data-go"); else e.dataset.go = "photos";
    });
    $$("[data-tpl]", bk).forEach(e => { e.innerHTML = esc(fill(e.dataset.tpl)).replace(/\n/g, "<br>"); });
    // the photo step's first line: where the preview goes (the answer just given), so the jump from the questions reads as progress
    const okLine = $(".bk-next-ok", bk);
    if (okLine) {
      const show = emailOk() && !childLocked();
      okLine.hidden = !show;
      const h = show ? `${icon("check", 15)}<span>${esc(fill(copy("emailThanks", "Thanks! We'll email {n}'s preview to ")))}<b>${esc(st.email)}</b>.</span>` : "";
      if (okLine._h !== h) { okLine.innerHTML = h; okLine._h = h; }
    }
    $$(".bk-tp-from", bk).forEach(e => (e.style.display = st.from ? "" : "none"));
    $$(".bk-tp-ded", bk).forEach(e => (e.style.display = st.dedication ? "" : "none"));
    const len = Math.max(3, (st.name || "NAME").length);
    $$(".bk-sand", bk).forEach(e => (e.style.fontSize = `min(${((parseFloat(e.style.width) * 0.9) / (len * 0.8)).toFixed(2)}cqw, 10cqw)`));
    $$(".bk-crayon", bk).forEach(e => (e.style.fontSize = `${clamp((parseFloat(e.dataset.w) * 1.4) / (len * 0.75), 1.4, 3.2).toFixed(2)}cqw`));
    if (LAYOUT.cover.fit) fitCover();
    fitTitle();
    paintAfter();
    renderBar();
  }

  /* A step that has just been done ticks with a small pop (.pop, for 900 ms); the chips are only redrawn when they change,
     so a redraw on every keystroke can't restart or cut the pop (or drop the focus from a chip). */
  let lastDone = null, chipsHTML = "";
  const popUntil = {};
  function renderBar() {
    if (!bar) return;
    const n = nextStep();
    const chips = $(".bk-chips", bar), now = performance.now();
    if (lastDone) STEPS.forEach(s => { if (s.done() && !lastDone.has(s.id)) popUntil[s.id] = now + 900; });
    lastDone = new Set(STEPS.filter(s => s.done()).map(s => s.id));
    const html = STEPS.map((s, i) => `<button type="button" class="bk-chip${s.done() ? " done" : ""}${s.done() && popUntil[s.id] > now ? " pop" : ""}${n && n.id === s.id ? " next" : ""}" data-step="${s.id}"${n && n.id === s.id ? ' aria-current="step"' : ""}
      aria-label="Step ${i + 1} of ${STEPS.length}: ${esc(fill(s.label))}, ${esc(s.done() ? "done" : s.value() || (i < 2 ? "to add" : "optional"))}">
      <i class="ck" aria-hidden="true"></i><span aria-hidden="true"><b class="lg">${esc(fill(s.label))}</b><b class="sh">${esc(s.short || s.label)}</b><small>${esc(s.value() || (i < 2 ? "Add" : ""))}</small></span></button>`).join("");
    if (html !== chipsHTML) { chips.innerHTML = html; chipsHTML = html; }
    // where the row still scrolls (a narrow desktop window), keep the current step in view
    const cur = $(".bk-chip.next", chips);
    if (cur && chips.scrollWidth > chips.clientWidth) chips.scrollLeft = Math.max(0, cur.offsetLeft - (chips.clientWidth - cur.offsetWidth) / 2);
    const c = $(".bk-cont", bar);
    if (c.dataset.busy) return;
    if (n) { c.innerHTML = "Continue"; c.classList.remove("buy"); }
    else { c.innerHTML = `Add ${esc(st.name)}'s book`; c.classList.add("buy"); }      // the price stands beside it, never in it
    bar.classList.toggle("all", !n);
  }

  /* Under the revealed cover: the portrait thumbnail, then only a quiet link (retry is a backup plan, not a button). */
  const mailHTML = () => `Email us at <span class="bk-mail"><span class="bk-addr">${esc(MAIL)}</span><button type="button" class="bk-copy" data-copy="${esc(MAIL)}">Copy</button></span>`;
  let afterKey = "";
  function paintAfter() {
    const box = $(".bk-after", bk); if (!box) return;
    const key = [st.revealed, st.name, st.portraitUrl, retryAvailable()].join("|");
    if (key === afterKey && box.firstChild) return;
    afterKey = key;
    if (!st.revealed) { box.innerHTML = ""; return; }
    const N = esc(st.name || "the child");
    box.innerHTML = `${st.portraitUrl ? `<figure class="bk-pt"><img src="${esc(st.portraitUrl)}" alt=""><figcaption>${N}, drawn in the book's style</figcaption></figure>` : ""}
      ${retryAvailable() ? `<p class="bk-quiet">Doesn't look like ${N}? <button type="button" class="bk-qlink" data-tips>See photo tips</button></p>`
                         : `<p class="bk-quiet">Still not quite right? ${mailHTML()} and we'll make it right.</p>`}
      <p class="bk-reassure">${esc(fill(copy("orderNote", "{N}'s book is made the moment you order and goes straight to print.")))}</p>`;
  }
  async function copyText(btn) {
    const txt = btn.dataset.copy; let ok = false;
    try { await navigator.clipboard.writeText(txt); ok = true; }
    catch (e) {
      const a = btn.parentElement.querySelector(".bk-addr");
      if (a) { const r = document.createRange(); r.selectNodeContents(a); const s = getSelection(); s.removeAllRanges(); s.addRange(r); try { ok = document.execCommand("copy"); } catch (e2) {} }
    }
    btn.textContent = ok ? "Copied ✓" : "Selected: press copy";
    clearTimeout(btn._t); btn._t = setTimeout(() => (btn.textContent = "Copy"), 2400);
  }

  // ---------------------------------------------------------------- modals
  let onClose = null;      // cleanup for the screen currently in the modal (crop queue, retry mode)
  /* One step after another in the same box (the modal is already up): the old step is kept for a moment as a ghost
     (moved, not copied; ids dropped, inert, hidden from screen readers, appended after the new step so every lookup finds
     the new one) and slides out while the box's height eases to the new step and the new step's blocks slide in. The
     header row (Cancel, "Step 2 of 4") stays still. A fresh open keeps the CSS rise (bkin) and staggers the blocks.
     navDir: -1 for a way back (Photo tips -> photos, Fix the spelling), so the content moves the other way. */
  let navDir = 1, mwAnims = null, opener = null;      // opener: what had the focus before the box opened (it gets it back)
  const goBack = fn => (...a) => { navDir = -1; try { return fn(...a); } finally { navDir = 1; } };
  function modalBlocks(m) {
    return [...m.children].filter(c => !c.classList.contains("bk-ghost") && !c.classList.contains("bk-mh"))
      .flatMap(c => (c.classList.contains("bk-mb") && c.children.length && c.children.length <= 10 ? [...c.children] : [c]))
      .filter(c => !c.hidden && c.getClientRects().length);
  }
  function modal(html, cls = "") {
    onClose = null;
    const dir = navDir; navDir = 1;
    const m = $(".bk-m", bk);
    const wasUp = mw.classList.contains("open") || mw.classList.contains("closing");
    endClosing();
    if (!wasUp) { const a = document.activeElement; opener = a && a !== document.body && !mw.contains(a) ? a : null; }
    const swap = wasUp && !calm() && bk.classList.contains("open") && m.childElementCount > 0;
    let from = 0, ghost = null;
    if (swap) {
      const r = m.getBoundingClientRect(), cs = getComputedStyle(m);
      from = r.height;
      $$(":scope > .bk-ghost", m).forEach(g => g.remove());
      ghost = document.createElement("div");
      ghost.className = "bk-ghost"; ghost.setAttribute("aria-hidden", "true"); ghost.inert = true;
      Object.assign(ghost.style, { width: r.width + "px", padding: cs.padding, top: -m.scrollTop + "px" });
      while (m.firstChild) ghost.appendChild(m.firstChild);
      $$("[id]", ghost).forEach(e => e.removeAttribute("id"));
    }
    m.className = "bk-m " + cls; m.innerHTML = fill(html, esc);
    /* Every step box has an ✕ (top right) so it's clear it can be closed at any point; it closes like a tap on the backdrop.
       A left-hand "Close" would only repeat it, so it is hidden (kept, for wireCancel); Cancel and Back stay. */
    const mh = $(":scope > .bk-mh", m);
    if (mh) {
      const left = $(".bk-cancel", mh);
      if (left && left.textContent.trim() === "Close") left.classList.add("bk-dup");
      mh.insertAdjacentHTML("beforeend", `<button type="button" class="bk-mx" aria-label="Close">✕</button>`);
      $(".bk-mx", mh).onclick = () => closeModal();
    }
    mw.classList.add("open"); live();
    m.scrollTop = 0;
    if (swap) {
      m.appendChild(ghost);
      const a = ghost.animate([{ opacity: 1, translate: "0 0" }, { opacity: 0, translate: `${-dir * SHIFT}px 0` }], { duration: T_OUT, easing: EASE_IN, fill: "forwards" });
      const drop = () => ghost.remove();
      a.onfinish = drop; setTimeout(drop, T_OUT + 250);       // also when a background tab holds the animation back
      tweenHeight(m, from);
      slideIn(modalBlocks(m), dir, { delay: 40 });
    } else if (!calm() && bk.classList.contains("open")) {
      slideIn(modalBlocks(m), 1, { axis: "y", dist: 10, delay: 50, step: 35 });
    }
    // the new step's heading takes the focus unless the step put it somewhere itself (Esc and Tab start from there)
    if (!m.contains(document.activeElement) || document.activeElement.closest(".bk-ghost")) focusHeading($("h2", m));
    return m;
  }
  /* closing: the box sinks a little as the backdrop fades (180 ms); "open" goes at once, so every check sees it closed */
  function hideMw() {
    if (!mw || !mw.classList.contains("open")) return;
    mw.classList.remove("open");
    const a = document.activeElement;
    if (opener && opener.isConnected && opener.getClientRects().length && (!a || a === document.body || mw.contains(a))) opener.focus({ preventScroll: true });
    opener = null;
    if (calm() || !bk.classList.contains("open")) return;
    mw.classList.add("closing"); mw.inert = true;
    const m = $(".bk-m", bk);
    mwAnims = [mw.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: EASE_IN, fill: "forwards" }),
               m.animate([{ translate: "0 0" }, { translate: "0 10px" }], { duration: 180, easing: EASE_IN, fill: "forwards" })];
    mwAnims[0].onfinish = endClosing;
  }
  function endClosing() {
    if (!mw) return;
    mw.classList.remove("closing"); mw.inert = false;
    if (mwAnims) { const a = mwAnims; mwAnims = null; a.forEach(x => x.cancel()); }
  }
  function showMw() { endClosing(); mw.classList.add("open"); }
  /* the end of the step in the modal (its timers and cleanup), with or without closing the box */
  function endScreen() {
    if (!genMin) { clearTimeout(genTimer); genRun++; }     // minimized: the generation keeps polling behind another step's modal
    const f = onClose; onClose = null; if (f) f();
    if (st.retryMode) cancelRetry();
  }
  function closeModal() { hideMw(); endScreen(); }
  const head = (id, title, sub = "") => {
    const i = STEPS.findIndex(s => s.id === id);
    return `<div class="bk-mh"><button type="button" class="bk-cancel">Cancel</button><span>Step ${i + 1} of ${STEPS.length}</span></div>
      <h2>${title}</h2>${sub ? `<p class="bk-msub">${sub}</p>` : ""}`;
  };
  const plainHead = (left, right, title, sub = "") => `<div class="bk-mh"><button type="button" class="bk-cancel">${left}</button><span>${right}</span></div>
      <h2>${title}</h2>${sub ? `<p class="bk-msub">${sub}</p>` : ""}`;
  function wireCancel(m, fn = closeModal) { $(".bk-cancel", m).onclick = fn; }
  /* Done with a step: the next one takes its place in the same box (no close-and-reopen gap). After the last one (the
     gift, also when it was changed from the check step) the box goes on to "Check {N}'s book", the step before the cart.
     A gift written while we draw goes back to the drawing (stepPreview resumes it). */
  function advance() {
    const n = nextStep();
    if (!n) { endScreen(); live(); stepCheck(); return; }
    if (n.id === "preview" && !photosReady()) { closeModal(); live(); return; }
    endScreen(); live(); openStep(n.id);
  }

  function openStep(id) {
    if (id === "child" && childLocked()) { stepChildLocked(); return; }
    if (id === "preview" && !photosReady()) id = "photos";
    if (id === "photos" && !st.childDone) id = "child";
    // after the reveal, new photos only go through the one retry, reached via the photo tips
    if (id === "photos" && st.revealed && !st.retryMode) { openTips("reveal"); return; }
    if (id === "preview" && st.revealed) { closeModal(); scrollTo("cover", true); return; }     // no second free preview
    ({ child: stepChild, photos: stepPhotos, preview: stepPreview, gift: stepGift, dedication: stepGift, from: stepGift })[id]();
  }

  function stepChild() {
    if (childLocked()) { stepChildLocked(); return; }              // every way in (Fix the spelling, the photo step) keeps the lock
    const m = modal(`${head("child", "Who is this book for?")}
      <div class="bk-mb">${childFields("mc")}</div>
      <div class="bk-mf"><button type="button" class="bk-btn" id="mc-go">Continue</button></div>`);
    wireCancel(m);
    const check = wireChild(m, "mc");
    $("#mc-go", m).onclick = () => { if (!check()) return; st.childDone = true; advance(); };
  }
  function stepChildLocked() {
    const g = st.gender === "girl" ? "Girl" : "Boy";
    const m = modal(`${plainHead("Close", "", "{N}'s details", "These are drawn into {N}'s book.")}
      <div class="bk-mb"><dl class="bk-locked"><dt>Name</dt><dd>{N}</dd><dt>Boy or girl</dt><dd>${g}</dd>
        <dt>Age</dt><dd>${esc(st.age ? ageLabel(st.age) : "Not set")}</dd><dt>Eye color</dt><dd>${esc(st.eyes || "Not set")}</dd></dl>
        <p class="bk-quiet">${icon("lock")} Need to change {n}'s details? ${mailHTML()}</p></div>
      <div class="bk-mf"><button type="button" class="bk-btn ghost" id="ml-ok">Back to the book</button></div>`, "locked");
    wireCancel(m); $("#ml-ok", m).onclick = closeModal;
  }

  // ---------------------------------------------------------------- photos: decode, crop, check
  /* Decode with EXIF orientation applied. createImageBitmap(..., {imageOrientation: "from-image"}) where supported;
     otherwise an <img>, which browsers draw EXIF-rotated (CSS image-orientation defaults to from-image). */
  async function decodeImage(blob) {
    if (window.createImageBitmap) {
      try { const b = await createImageBitmap(blob, { imageOrientation: "from-image" }); return { src: b, w: b.width, h: b.height, close: () => b.close && b.close() }; }
      catch (e) { /* option unsupported or undecodable: try <img> */ }
    }
    const url = URL.createObjectURL(blob), img = new Image();
    img.src = url;
    try { await img.decode(); } catch (e) { URL.revokeObjectURL(url); throw e; }
    return { src: img, w: img.naturalWidth, h: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  }
  function toJpeg(src, sx, sy, sw, sh, max, q) {
    const k = Math.min(1, max / Math.max(sw, sh)), w = Math.max(1, Math.round(sw * k)), h = Math.max(1, Math.round(sh * k));
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const x = c.getContext("2d");
    x.fillStyle = "#fff"; x.fillRect(0, 0, w, h);
    x.imageSmoothingEnabled = true; x.imageSmoothingQuality = "high";
    x.drawImage(src, sx, sy, sw, sh, 0, 0, w, h);
    return new Promise((res, rej) => c.toBlob(b => (b ? res({ blob: b, w, h }) : rej(new Error("toBlob failed"))), "image/jpeg", q));
  }
  const validBox = b => Array.isArray(b) && b.length === 4 && b.every(v => isFinite(v)) && b[2] - b[0] > 0.02 && b[3] - b[1] > 0.02
    ? [clamp(+b[0], 0, 1), clamp(+b[1], 0, 1), clamp(+b[2], 0, 1), clamp(+b[3], 0, 1)] : null;

  /* A new photo: decode once, make the ~1600px copy (shown in the crop screen AND sent to photos/check, so both
     share one orientation), and start the check. The original File stays untouched for the full-res crop. */
  async function preparePhoto(blob) {
    const im = await decodeImage(blob);
    let view;
    try { view = await toJpeg(im.src, 0, 0, im.w, im.h, CHECK_MAX, 0.86); } finally { im.close(); }
    const p = { uid: uuid(), file: blob, w: im.w, h: im.h, view: URL.createObjectURL(view.blob), check: null };
    p.checking = PersonalizeAPI.checkPhoto(view.blob, { width: view.w, height: view.h, srcWidth: im.w, srcHeight: im.h })
      .then(r => { p.check = { ok: r && r.ok !== false, reason: r && r.reason, faces: (r && r.faces) || [], suggested_crop: r && r.suggested_crop }; })
      .catch(err => { console.warn("[pb] photos/check failed; using a centered box", err); p.check = { ok: true, faces: [], suggested_crop: null, failed: true }; });
    return p;
  }
  function cropStats(p, b) {
    const sw = (b[2] - b[0]) * p.w, sh = (b[3] - b[1]) * p.h, short = Math.min(sw, sh);
    const faces = ((p.check && p.check.faces) || []).filter(f => {
      const cx = (f.box[0] + f.box[2]) / 2, cy = (f.box[1] + f.box[3]) / 2;
      return cx > b[0] && cx < b[2] && cy > b[1] && cy < b[3];
    });
    let facePx = null;
    faces.forEach(f => { const s = Math.max((f.box[2] - f.box[0]) * p.w, (f.box[3] - f.box[1]) * p.h); facePx = Math.max(facePx || 0, s); });
    const block = short < MIN_SIDE;
    // with a known face: its size; without one, assume the face is about half a tight crop
    const warn = !block && (facePx != null ? facePx < FACE_WARN : short < FACE_WARN * 2);
    return { sw, sh, short, facePx, block, warn };
  }
  /* Crop the ORIGINAL at full resolution (EXIF-oriented), shrink to <= 3000 px, JPEG 0.9; held until POST /previews. */
  async function applyCrop(p, box) {
    const im = await decodeImage(p.file);
    try {
      const sx = Math.round(box[0] * im.w), sy = Math.round(box[1] * im.h);
      const sw = Math.max(1, Math.round((box[2] - box[0]) * im.w)), sh = Math.max(1, Math.round((box[3] - box[1]) * im.h));
      const out = await toJpeg(im.src, sx, sy, sw, sh, CROP_MAX, 0.9);
      return { ...p, uid: uuid(), crop: box.slice(), blob: out.blob, bw: out.w, bh: out.h, thumb: URL.createObjectURL(out.blob), small: cropStats(p, box).warn };
    } finally { im.close(); }
  }
  const reasonText = r => ({ no_face: "We couldn't find a face in this photo. A photo of {n} facing the camera works best.",
    blurry: "This photo looks a little blurry. A sharper one will look better.", too_dark: "This photo is quite dark. One taken in better light will look better.",
    too_small: "This photo is small; a closer photo will look better." }[r] || (/\s/.test(r || "") ? r : "This photo may not work well. A clear photo of {n} facing the camera works best."));

  /* The crop screen: drag inside the box to move it, corner handles to resize, two-finger pinch to scale,
     arrow keys (Shift = resize) for keyboards. done(newPhoto|null, "ok"|"skip"|"cancel"|"closed"). */
  function stepCrop(p, o) {
    let finished = false, busy = false, touched = !!p.crop;
    const m = modal(`<div class="bk-mh"><button type="button" class="bk-cancel">Cancel</button><span>${o.total > 1 ? `Photo ${o.idx} of ${o.total}` : o.edit ? "Edit crop" : st.retryMode ? "One more try" : `Step ${STEPS.findIndex(x => x.id === "photos") + 1} of ${STEPS.length}`}</span></div>
      <h2>Crop to just {n}</h2>
      <p class="bk-msub">Drag the box over {n}'s face and shoulders. Pull a corner to make it bigger or smaller.</p>
      <div class="bk-cropwrap"><div class="bk-crop" tabindex="0" role="group" aria-label="Crop box. Arrow keys move it; Shift and arrow keys resize it.">
        <img src="${p.view}" alt="" draggable="false"><div class="bk-faces"></div>
        <div class="bk-cbox"><i data-h="nw"></i><i data-h="ne"></i><i data-h="sw"></i><i data-h="se"></i></div>
        <div class="bk-cfind"><i class="bk-spin sm"></i>Finding {n}…</div></div></div>
      <p class="bk-facehint" hidden>We found more than one face. Tap {n}'s face to crop to it.</p>
      <p class="bk-cmsg" aria-live="polite"></p>
      <div class="bk-mf two"><button type="button" class="bk-btn ghost" id="cr-no">${o.edit ? "Cancel" : o.total > 1 ? "Skip this photo" : "Cancel"}</button><button type="button" class="bk-btn" id="cr-ok">${esc(copy("cropButton", "Crop to just {n}"))}</button></div>`, "crop");
    const crop = $(".bk-crop", m), boxEl = $(".bk-cbox", crop), msg = $(".bk-cmsg", m), ok = $("#cr-ok", m), wrap = $(".bk-cropwrap", m);
    const A = p.w / p.h;
    const size = () => {
      const W = wrap.clientWidth || 300, maxH = Math.max(220, Math.min(innerHeight * (phone() ? 0.5 : 0.5), 520));
      let w = W, h = w / A; if (h > maxH) { h = maxH; w = h * A; }
      crop.style.width = Math.round(w) + "px"; crop.style.height = Math.round(h) + "px";
    };
    const finish = (v, how) => {
      if (finished) return; finished = true;
      removeEventListener("resize", size);
      o.done(v, how);
    };
    onClose = () => finish(null, "closed");
    addEventListener("resize", size); size();

    let box = p.crop ? p.crop.slice() : centeredBox(p.w, p.h);
    const judge = () => {
      const s = cropStats(p, box);
      ok.disabled = s.block || busy;
      // photos/check may send a reason hint (no_face, blurry, too_dark, too_small) with ok true or false: always show it
      const hint = p.check && (p.check.reason || !p.check.ok) ? fill(reasonText(p.check.reason)) : "";
      msg.className = "bk-cmsg" + (s.block ? " bad" : s.warn || hint ? " warn" : "");
      msg.textContent = s.block ? fill("This crop is too small to draw {n} well. Make the box bigger, or use a closer photo.")
        : [hint, s.warn && !(p.check && p.check.reason === "too_small") ? "This crop is small; a closer photo will look better." : ""].filter(Boolean).join(" ");
    };
    const render = () => {
      const [x0, y0, x1, y1] = box;
      Object.assign(boxEl.style, { left: x0 * 100 + "%", top: y0 * 100 + "%", width: (x1 - x0) * 100 + "%", height: (y1 - y0) * 100 + "%" });
      judge();
    };
    const animateTo = b => { box = b; boxEl.classList.add("anim"); render(); setTimeout(() => boxEl.classList.remove("anim"), 420); };
    const applyCheck = () => {
      if (finished) return;
      const c = p.check || {};
      $(".bk-cfind", crop).hidden = true;
      const faces = c.faces || [];
      if (faces.length > 1) {
        $(".bk-faces", crop).innerHTML = faces.map((f, i) => { const [a, b, x, y] = f.box;
          return `<button type="button" class="bk-face" data-face="${i}" aria-label="Crop to face ${i + 1}" style="left:${a * 100}%;top:${b * 100}%;width:${(x - a) * 100}%;height:${(y - b) * 100}%"></button>`; }).join("");
        $(".bk-facehint", m).hidden = false;
      }
      if (!touched) animateTo(validBox(c.suggested_crop) || centeredBox(p.w, p.h)); else judge();
    };
    render();
    if (p.check) applyCheck(); else p.checking.then(applyCheck);

    const toFace = i => { if (!p.check || !p.check.faces[i]) return; touched = true;
      animateTo(cropAroundFace(p.check.faces[i].box, p.w, p.h, p.check.faces.map(x => x.box))); };
    // Keyboard only (Tab to a face, Enter/Space): pointer taps are handled below, since the face outlines sit on top of
    // the crop box and a drag that starts on one must still move the box.
    $(".bk-faces", crop).addEventListener("click", e => {
      const f = e.target.closest("[data-face]"); if (!f || e.detail !== 0) return;
      toFace(+f.dataset.face);
    });

    // ---- pointer interaction (mouse, pen and touch share one path)
    const ptrs = new Map(); let mode = null, p0 = null, b0 = null, d0 = 0, tap = null;
    const inBox = e => { const r = crop.getBoundingClientRect(), x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
      return x >= box[0] && x <= box[2] && y >= box[1] && y <= box[3]; };
    const pt = e => ({ x: e.clientX, y: e.clientY });
    const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y) || 1;
    const minW = () => 44 / crop.clientWidth, minH = () => 44 / crop.clientHeight;
    const startSingle = (q, h) => { mode = h || "move"; p0 = q; b0 = box.slice(); };
    crop.addEventListener("pointerdown", e => {
      const faceEl = e.target.closest(".bk-face");
      if (busy || (!e.target.closest(".bk-cbox") && !faceEl)) return;
      e.preventDefault();
      try { crop.setPointerCapture(e.pointerId); } catch (err) {}
      ptrs.set(e.pointerId, pt(e)); boxEl.classList.remove("anim");
      tap = faceEl && ptrs.size === 1 ? { i: +faceEl.dataset.face, q: pt(e) } : null;       // a tap on a face picks it
      if (ptrs.size >= 2) { const [a, b] = [...ptrs.values()]; mode = "pinch"; d0 = dist(a, b); b0 = box.slice(); tap = null; }
      else if (faceEl && !inBox(e)) mode = null;                                          // outside the box: tap only
      else startSingle(pt(e), faceEl ? undefined : e.target.dataset.h);                   // inside: a drag still moves the box
      if (!faceEl) touched = true;
      if (mode) crop.classList.add("drag");
    });
    crop.addEventListener("pointermove", e => {
      if (!ptrs.has(e.pointerId)) return;
      ptrs.set(e.pointerId, pt(e));
      if (tap && dist(tap.q, pt(e)) > 6) tap = null;
      if (!mode || tap) return;
      touched = true;
      const W = crop.clientWidth, H = crop.clientHeight;
      let [x0, y0, x1, y1] = b0;
      if (mode === "pinch") {
        const [a, b] = [...ptrs.values()], bw = x1 - x0, bh = y1 - y0;
        const k = clamp(dist(a, b) / d0, Math.max(minW() / bw, minH() / bh), Math.min(1 / bw, 1 / bh));
        const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2, w = bw * k, h = bh * k;
        const nx = clamp(cx - w / 2, 0, 1 - w), ny = clamp(cy - h / 2, 0, 1 - h);
        box = [nx, ny, nx + w, ny + h];
      } else {
        const q = pt(e), dx = (q.x - p0.x) / W, dy = (q.y - p0.y) / H;
        if (mode === "move") {
          const w = x1 - x0, h = y1 - y0, nx = clamp(x0 + dx, 0, 1 - w), ny = clamp(y0 + dy, 0, 1 - h);
          box = [nx, ny, nx + w, ny + h];
        } else {
          if (mode.includes("w")) x0 = clamp(b0[0] + dx, 0, b0[2] - minW());
          if (mode.includes("e")) x1 = clamp(b0[2] + dx, b0[0] + minW(), 1);
          if (mode.includes("n")) y0 = clamp(b0[1] + dy, 0, b0[3] - minH());
          if (mode.includes("s")) y1 = clamp(b0[3] + dy, b0[1] + minH(), 1);
          box = [x0, y0, x1, y1];
        }
      }
      render();
    });
    const up = e => {
      if (!ptrs.delete(e.pointerId)) return;
      if (tap && e.type === "pointerup" && !ptrs.size) { const i = tap.i; tap = null; mode = null; crop.classList.remove("drag"); toFace(i); return; }
      tap = null;
      if (ptrs.size === 1) startSingle([...ptrs.values()][0], "move");      // pinch -> one finger keeps dragging
      else if (!ptrs.size) { mode = null; crop.classList.remove("drag"); }
    };
    crop.addEventListener("pointerup", up); crop.addEventListener("pointercancel", up);
    crop.addEventListener("keydown", e => {
      const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key]; if (!d || busy) return;
      e.preventDefault(); touched = true;
      const s = 0.015; let [x0, y0, x1, y1] = box;
      if (e.shiftKey) { x1 = clamp(x1 + d[0] * s, x0 + minW(), 1); y1 = clamp(y1 + d[1] * s, y0 + minH(), 1); box = [x0, y0, x1, y1]; }
      else { const w = x1 - x0, h = y1 - y0, nx = clamp(x0 + d[0] * s, 0, 1 - w), ny = clamp(y0 + d[1] * s, 0, 1 - h); box = [nx, ny, nx + w, ny + h]; }
      render();
    });

    $(".bk-cancel", m).onclick = () => finish(null, "cancel");
    $("#cr-no", m).onclick = () => finish(null, !o.edit && o.total > 1 ? "skip" : "cancel");
    ok.onclick = async () => {
      busy = true; ok.disabled = true; ok.innerHTML = `<i class="bk-spin sm light"></i> Cropping…`;
      try { finish(await applyCrop(p, box), "ok"); }
      catch (err) {
        console.error("[pb] crop failed", err); busy = false; ok.textContent = fill(copy("cropButton", "Crop to just {n}")); judge();
        msg.className = "bk-cmsg bad"; msg.textContent = "Sorry, we couldn't crop that photo. Please try another one.";
      }
    };
  }
  const cropFlow = (p, o) => new Promise(res => stepCrop(p, { ...o, done: (v, how) => res([v, how]) }));

  let adding = false;
  async function addBlobs(items) {      // [{ blob }] -> a crop screen for each, in turn
    items = items.slice(0, 4 - st.photos.length);
    if (!items.length) return;
    adding = true; paintPhotos && paintPhotos();
    for (let i = 0; i < items.length; i++) {
      let p;
      try { p = await preparePhoto(items[i].blob); }
      catch (err) { console.warn("[pb] couldn't open photo", err); toast("We couldn't open one of those photos. Please try a JPEG or PNG."); continue; }
      const [np, how] = await cropFlow(p, { idx: i + 1, total: items.length });
      if (np) st.photos.push(np);
      if (how === "closed") { adding = false; live(); return; }
      if (how === "cancel") break;
    }
    adding = false;
    stepPhotos(); live();
  }
  async function editCrop(i) {
    const [np, how] = await cropFlow(st.photos[i], { idx: 1, total: 1, edit: true });
    if (how === "closed") return;
    if (np) st.photos[i] = np;
    stepPhotos(); live();
  }
  const SAMPLE = DEMO_BOOK.samples ? DEMO_BOOK.samples.map(U) : [2, 3, 4, 1].map(i => U("boaz-photo" + i));
  const fetchBlob = async u => { const r = await fetch(u); if (!r.ok) throw new Error("sample " + r.status); return r.blob(); };

  /* Drag and drop (desktop): image files dropped anywhere on the photo step's upload area go through addBlobs, exactly
     like the file picker (same limit, order, decoding and crop screens). Anything that isn't an image is left out, with
     the step's warning line. Touch devices never fire these events. */
  const IMAGE_EXT = /\.(jpe?g|png|gif|webp|avif|heic|heif|bmp|tiff?)$/i;
  const isImage = f => /^image\//i.test(f.type || "") || (!f.type && IMAGE_EXT.test(f.name || ""));
  const hasFiles = e => !!(e.dataTransfer && [...(e.dataTransfer.types || [])].includes("Files"));
  let dropNote = "";
  function wireDrop(zone, msg, canAdd, onBlocked) {
    const off = () => zone.classList.remove("bk-drop");
    const say = t => { msg.textContent = t; msg.hidden = !t; dropNote = t; };     // dropNote: shown again after the crop screens
    if (dropNote) { say(dropNote); dropNote = ""; }
    zone.addEventListener("dragenter", e => { if (!hasFiles(e)) return; e.preventDefault(); if (canAdd()) zone.classList.add("bk-drop"); });
    zone.addEventListener("dragover", e => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = canAdd() ? "copy" : "none";
      if (canAdd()) zone.classList.add("bk-drop");
    });
    zone.addEventListener("dragleave", e => { if (!zone.contains(e.relatedTarget)) off(); });
    zone.addEventListener("drop", e => {
      if (!hasFiles(e)) return;
      e.preventDefault(); off();
      if (!canAdd()) { onBlocked && onBlocked(); return; }
      dropNote = "";
      const files = [...e.dataTransfer.files], imgs = files.filter(isImage), skipped = files.length - imgs.length;
      say(skipped ? (imgs.length ? `${skipped === 1 ? "One file wasn't a photo" : `${skipped} files weren't photos`}, so we left ${skipped === 1 ? "it" : "them"} out.`
                                 : "That isn't a photo. Please drop a JPEG or PNG photo.") : "");
      if (imgs.length) addBlobs(imgs.map(f => ({ blob: f }))); else dropNote = "";
    });
  }

  // the photo guide's badges: a green tick on the good example, a red cross on the two to avoid
  const tipBadge = good => good ? '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="#57ad4e" stroke="#fff" stroke-width="2"/><path d="M7 12.5l3 3 7-7" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round"/></svg>' : '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="#e0533f" stroke="#fff" stroke-width="2"/><path d="M8 8l8 8M16 8l-8 8" stroke="#fff" stroke-width="2.4" stroke-linecap="round"/></svg>';
  let paintPhotos = null;
  function stepPhotos() {
    const R = st.retryMode, FB = !R && st.photoFeedback;
    // the email comes first, before any photo leaves the device. Not for the one retry or a locked preview: the retry
    // sends no email, and a shopper back from an email link has a preview but no email here (the status carries none)
    if (!emailOk() && !R && !childLocked()) { stepChild(); return; }
    // the photo guide always shows (the section sends its three default photos when the settings are blank); while it
    // does, the bullet list that repeats it stays out
    const guide = CFG.guide && CFG.guide.length === 3 ? CFG.guide : null;
    const bad = FB ? FB.filter(f => !f.accepted) : [];
    const m = modal(`${R ? plainHead("Cancel", "One more try", "Try once more with new photos", "Swap in clearer photos, or crop tighter to just {n}. You can redo the preview once.")
                         : head("photos", "Add photos of {n}", "1 to 4 clear photos. You'll crop each one to just {n}.")}
      <div class="bk-mb">${FB ? `<div class="bk-fb" role="alert"><p><b>We couldn't use these photos.</b> A clear photo of just {n}, face visible and in focus, works best.</p>
          ${bad.length ? `<ul>${bad.map(f => `<li>Photo ${esc(f.photo)}: ${esc((f.problems || []).join("; ") || "we couldn't use this one")}</li>`).join("")}</ul>` : ""}</div>` : ""}
        <label class="bk-consent bk-phconsent"><input type="checkbox" id="mp-consent"${st.consent ? " checked" : ""}><span id="mp-consent-t"></span></label>
        <p class="bk-conhint" hidden>Tick the box above first, then add your photos.</p>
        <div class="bk-phgrid"></div><p class="bk-cmsg warn bk-dropmsg" role="alert" hidden></p><p class="bk-cmsg warn bk-smallnote" hidden>A crop marked Small may look soft; a closer photo will look better.</p>
        <div class="bk-phrow"><label class="bk-link" id="mp-up">⬆ Upload from your device<input type="file" accept="image/*" multiple hidden id="mp-file"></label>
          ${PersonalizeAPI.demo && (CFG.samplePhotos !== false || /[#&]samples\b/.test(location.hash)) ? `<button type="button" class="bk-link" id="mp-sample">Use the sample photos</button>` : ""}
          <button type="button" class="bk-link" id="mp-tips">Photo tips</button></div>
        ${guide ? `<p class="bk-guide-h" id="bk-guide-h">What works best</p><ul class="bk-guide" aria-labelledby="bk-guide-h">${guide.map(g => `<li class="${g.good ? "do" : "dont"}">
          <span class="bk-guide-img"><img src="${esc(g.url)}" alt="">${tipBadge(g.good)}</span><span><span class="bk-sr">${g.good ? "Good example:" : "Avoid:"} </span>${esc(g.label)}</span></li>`).join("")}</ul>` : ""}
        ${guide ? "" : `<ul class="bk-tips"><li>Face the camera, in good light</li><li>Just {n}: no hats or sunglasses</li><li>Recent photos</li></ul>`}</div>
      <div class="bk-mf"><p class="bk-mfnote" hidden></p><button type="button" class="bk-btn" id="mp-go">${R ? esc(copy("redrawButton", "Draw {n} again")) : "Continue"}</button></div>`);
    wireCancel(m);
    if (R) onClose = cancelRetry;
    const grid = $(".bk-phgrid", m), go = $("#mp-go", m), note = $(".bk-mfnote", m), hint = $(".bk-conhint", m), body = $(".bk-mb", m);
    /* The photo permission (consent_version) is asked here, right above the photos: nothing can be added (picker, drop,
       sample photos) and nothing is sent until it is ticked. It stays ticked for the session (st.consent). */
    const ct = $("#mp-consent-t", m); ct.dataset.tpl = CONSENT; ct.innerHTML = esc(fill(CONSENT));
    const nudge = () => { hint.hidden = false; hint.classList.remove("flash"); void hint.offsetWidth; hint.classList.add("flash"); $("#mp-consent", m).focus(); };
    $("#mp-consent", m).onchange = e => { st.consent = e.target.checked; if (st.consent) hint.hidden = true; gate(); live(); };
    body.addEventListener("click", e => {                       // a tap on a disabled photo box or upload link: say why
      if (st.consent || !e.target.closest(".bk-ph.empty, #mp-up, #mp-sample")) return;
      e.preventDefault(); nudge();
    }, true);
    const gate = () => {
      const unchanged = (R || FB) && sig() === st.sentSig;     // the same photos again would fail the same way
      body.classList.toggle("bk-noconsent", !st.consent);
      $$("input[type=file]", m).forEach(i => (i.disabled = !st.consent));
      const sbt = $("#mp-sample", m); if (sbt) sbt.setAttribute("aria-disabled", String(!st.consent));
      go.disabled = !st.photos.length || adding || unchanged || !st.consent;
      note.hidden = !(unchanged && st.photos.length && !adding);
      note.textContent = "Add a new photo or re-crop one first.";
      $("#mp-up", m).hidden = st.photos.length >= 4;
      $(".bk-smallnote", m).hidden = !st.photos.some(p => p.small);
    };
    const paint = () => {
      if (!grid.isConnected) return;
      grid.innerHTML = [0, 1, 2, 3].map(i => {
        const p = st.photos[i];
        if (!p) return i === st.photos.length && adding ? `<div class="bk-ph busy"><i class="bk-spin"></i><span>Opening…</span></div>`
          : `<label class="bk-ph empty"><input type="file" accept="image/*" multiple hidden class="mp-more"><b>+</b><span>Add photo</span></label>`;
        const rejected = FB && sig() === st.sentSig && bad.some(f => +f.photo === i + 1);
        return `<div class="bk-ph${p.small ? " small" : ""}${rejected ? " rejected" : ""}"><img src="${p.thumb}" alt="Photo ${i + 1}">
          ${rejected ? `<span class="flag bad">Try another</span>` : p.small ? `<span class="flag" title="This crop is small; a closer photo will look better.">Small</span>` : ""}
          <button type="button" class="rm" data-rm="${i}" aria-label="Remove photo ${i + 1}">×</button>
          <button type="button" class="ed" data-ed="${i}">Edit crop</button></div>`;
      }).join("");
      $$(".mp-more", grid).forEach(inp => (inp.onchange = onFiles));
      $$("[data-rm]", grid).forEach(b => (b.onclick = () => { st.photos.splice(+b.dataset.rm, 1); paint(); live(); }));
      $$("[data-ed]", grid).forEach(b => (b.onclick = () => editCrop(+b.dataset.ed)));
      gate();
    };
    paintPhotos = paint;
    function onFiles(e) { const fs = [...e.target.files]; e.target.value = ""; addBlobs(fs.map(f => ({ blob: f }))); }
    $("#mp-file", m).onchange = onFiles;
    wireDrop(body, $(".bk-dropmsg", m), () => st.consent && !adding && st.photos.length < 4, () => { if (!st.consent) nudge(); });
    $("#mp-tips", m).onclick = () => openTips("photos");
    const sb = $("#mp-sample", m);
    if (sb) sb.onclick = async () => {
      if (!st.consent) return;
      sb.disabled = true;
      try { const blobs = await Promise.all(SAMPLE.map(fetchBlob)); st.photos = []; addBlobs(blobs.map(blob => ({ blob }))); }
      catch (err) { console.error(err); sb.disabled = false; toast("Couldn't load the sample photos."); }
    };
    go.onclick = () => { if (R) stepGen("retry"); else advance(); };
    paint();
  }

  // ---------------------------------------------------------------- the free preview, the reveal, the one retry
  /* One random id per browser, for the edge's "2 previews per device" limit (D5). localStorage may be missing or
     throw (private mode, blocked storage): then one id lives in memory for this page. Never logged. */
  const DEVICE_KEY = "pb_device";
  let memDevice = null;
  function deviceId() {
    if (DEMO_BOOK.only) return memDevice ||= uuid();
    try { const v = localStorage.getItem(DEVICE_KEY); if (v && /^[\w-]{8,128}$/.test(v)) return v; } catch (e) {}
    memDevice = memDevice || uuid();
    try { localStorage.setItem(DEVICE_KEY, memDevice); } catch (e) {}
    return memDevice;
  }
  const childPayload = () => ({ name: st.name, gender: st.gender, age: String(st.age), eyes: st.eyes,
                                email: st.email, consent: true, consent_version: CONSENT_VERSION, device_id: deviceId(),
                                ...(CFG.book ? { book: CFG.book } : {}) });     // no book: the edge's default (Apes)
  if (PersonalizeAPI.demo) Object.assign(window, { __pbResume: resume, __pbChildPayload: childPayload, __pbCartProperties: cartProperties });   // review tests only
  let genTimer, genRun = 0;
  function stepPreview(phase = "intro", frozenPct) {
    if (phase === "gen" || st.pending) { stepGen(null, frozenPct); return; }       // a job is already running: resume it
    const m = modal(`${head("preview", "See {n} in the book, free", copy("previewIntro", "We'll draw {n} in the book's illustration style and put {him} on the cover. It takes about a minute. No payment needed."))}
      <div class="bk-mb"><div class="bk-thumbs">${st.photos.map(p => `<img src="${p.thumb}" alt="">`).join("")}</div>
        <div class="bk-spell"><p>We'll print {n}'s name exactly like this:</p><p class="bk-spell-n">{N}</p><p>Is it spelled right?</p></div>
        <div class="bk-free">${icon("eye")}&nbsp;${esc(copy("previewFree", "The preview is free. {N}'s book is made the moment you order and goes straight to print."))}</div></div>
      <div class="bk-mf"><button type="button" class="bk-btn" id="pv-go">${esc(copy("drawButton", "Yes, draw {n}"))}</button>
        <button type="button" class="bk-link" id="pv-fix">Fix the spelling</button></div>`);
    wireCancel(m);
    $("#pv-go", m).onclick = () => stepGen("new");
    $("#pv-fix", m).onclick = goBack(() => openStep("child"));      // through openStep: the childLocked() guard still applies
  }
  /* kind: "new" (POST /previews), "retry" (POST /previews/:id/retry) or null (resume polling st.pending). */
  /* While we draw (the sheets, then the cover) the progress modal can be minimized to a pill so the customer can browse
     the book: the same polling keeps going and feeds the pill. A stage that ends while minimized never pops a modal over
     the book: the pill turns green and offers the next step ("Pick one" / "See it"), or says something went wrong.
     Minimizing is per page view: a reload or a ?preview= link shows the progress modal as before. */
  let genMin = false, genPillEl = null;
  const genOpen = () => !!(mw && mw.classList.contains("open") && $(".bk-m", bk).classList.contains("gen") && st.pending);
  function genPill() {
    if (!genPillEl) {
      genPillEl = el(`<button type="button" class="bk-genpill" hidden><i class="bk-spin sm light" aria-hidden="true"></i><span class="t"></span><b class="go"></b></button>`);
      genPillEl.onclick = () => { const go = genPillEl._go; genMin = false; genPillEl.hidden = true; bk.classList.remove("gen-min"); go ? go() : restoreGen(); };
      bk.append(genPillEl, el(`<div class="bk-sr" role="status" aria-live="polite"></div>`));
    }
    genPillEl.style.bottom = (bar && bar.offsetHeight ? bar.offsetHeight + 12 : 20) + "px";   // above the step tabs and Continue
    return genPillEl;
  }
  function pillProgress(pct, stage) {
    if (!genMin) return;
    const p = genPill(); if (p._go) return;
    p.className = "bk-genpill"; p.hidden = false;
    $(".t", p).textContent = fill(stage >= 2 ? "Painting {n} onto the cover" : "Drawing {n}") + `… ${Math.round(pct || 0)}%`;
    $(".go", p).textContent = "";
  }
  function pillDone(text, go, bad) {         // the stage ended while minimized: offer the next step, announce it politely
    const p = genPill(); p._go = go; p.hidden = false;
    p.className = "bk-genpill " + (bad ? "bad" : "done");
    $(".t", p).textContent = fill(text[0]); $(".go", p).textContent = fill(text[1]);
    const sr = $(".bk-sr[role=status]", bk); if (sr) sr.textContent = fill(text[0] + " " + text[1]);
  }
  function minimizeGen() {
    if (!genOpen()) return;
    asideGen(); hideMw();
  }
  /* The drawing goes on behind the pill: before the book is shown (minimizeGen) or the gift step takes the box's place
     (the progress modal's "Write a dedication" / "Add who it's from"). */
  function asideGen() {
    if (!genOpen()) return;
    const bar2 = $(".bk-m .bk-bar2 i", bk), now = $$(".bk-m .bk-genlist li", bk).findIndex(li => li.classList.contains("now"));
    genMin = true; bk.classList.add("gen-min");
    const p = genPill(); p._go = null;
    pillProgress(parseFloat(bar2 && bar2.style.width) || 0, now < 0 ? 0 : now);
  }
  function restoreGen() {                    // reopen the progress modal (it kept updating while hidden)
    if ($(".bk-m", bk).classList.contains("gen") && st.pending) { showMw(); $(".bk-gen-min", bk)?.focus(); }
    else if (st.pending) stepGen(null);
  }
  const whileMin = (text, go, bad) => { if (!genMin) return false; pillDone(text, go, bad); return true; };

  async function stepGen(kind, frozenPct) {
    genMin = false; if (genPillEl) { genPillEl.hidden = true; genPillEl._go = null; } bk.classList.remove("gen-min");
    const msgs = kind === "retry" || (st.pending && st.pending.attempt > 1)
      ? ["Studying the new photos", "Drawing {n} again", "Painting {n} onto the cover"]
      : ["Studying {n}'s photos", "Drawing {n} in the book's style", "Painting {n} onto the cover"];
    const m = modal(`<button type="button" class="bk-gen-x" aria-label="Minimize and browse the book">✕</button>
      <div class="bk-gen"><div class="bk-gen-art" style="background-image:url('${U("ph-cover")}')">${LAYOUT.cover.fit ? coverElsHTML() : LAYOUT.cover.els.map(e => elHTML(e, CVW, CVH, { cls: "bk-cvtext" })).join("")}<div class="shim"></div></div>
      <h2>Drawing {n}…</h2><div class="bk-bar2"><i></i></div>
      <ul class="bk-genlist">${msgs.map(t => `<li>${t}</li>`).join("")}</ul>
      <p class="bk-fine">While we draw, you can read the book or write the gift lines; we'll let you know here when it's ready. ${esc(copy("progressEmail", "We'll also email you."))}</p>
      <div class="bk-gen-acts"><button type="button" class="bk-btn ghost bk-gen-min">Read the book</button>
        <button type="button" class="bk-btn ghost" data-gift="mg-d">${st.dedication ? "Change the dedication" : "Write a dedication"}</button>
        <button type="button" class="bk-btn ghost" data-gift="mg-f">${st.from ? "Change who it's from" : "Add who it's from"}</button></div></div>`, "gen");
    $(".bk-gen-x", m).onclick = $(".bk-gen-min", m).onclick = minimizeGen;
    $$("[data-gift]", m).forEach(b => (b.onclick = () => { const f = b.dataset.gift; asideGen(); stepGift(f); }));
    const bar2 = $(".bk-bar2 i", m), lis = $$(".bk-genlist li", m);
    const setP = (pct, stage) => { bar2.style.width = pct + "%"; lis.forEach((li, i) => { li.className = i < stage || pct >= 100 ? "done" : i === stage ? "now" : ""; });
      pillProgress(pct, stage); };
    if (frozenPct != null) { setP(frozenPct, Math.min(2, Math.floor(frozenPct / 34))); return; }
    const run = ++genRun;
    try {
      if (kind === "new") {
        st.creating = true;
        try {
          const job = await PersonalizeAPI.createPreview({ child: childPayload(), photos: st.photos.map(p => p.blob) });
          st.pending = { id: job.preview_id, attempt: 1 }; st.sentSig = sig(); st.photoFeedback = null;
        } finally { st.creating = false; }       // failed: no preview was made, the details unlock again
      } else if (kind === "retry") {
        const photos = st.photos.map(p => p.blob), s = sig();
        st.retryMode = false;                       // the request is on its way; closing now must not restore old photos
        const r = await PersonalizeAPI.retry(st.previewId, photos, deviceId(), st.consent ? CONSENT_VERSION : null);
        st.pending = { id: r.preview_id || st.previewId, attempt: 2 }; st.sentSig = s; st.sentPhotos = null;
        if (r.retries_left != null) st.retriesLeft = +r.retries_left;
      }
      if (run !== genRun || !st.pending) return;
      const poll = async () => {
        const s = await PersonalizeAPI.getStatus(st.pending.id);
        if (run !== genRun) return;
        if (s.attempt) st.pending.attempt = +s.attempt;          // the server's run number (sent back on pick, and in the cart)
        setP(s.progress || 0, s.stage || 0);
        if (s.status === "awaiting_pick") { if (!whileMin(["Your 2 drawings are ready.", "Pick one"], () => stepPick(s))) stepPick(s); return; }
        if (s.status === "ready") {
          setP(100, 3);
          if (!whileMin(["{N} is on the cover!", "See it"], () => finishGen(s))) genTimer = setTimeout(() => run === genRun && finishGen(s), 350);
        }
        else if (s.status === "failed") { if (!whileMin(["Something went wrong.", "Tap to see"], () => previewFailed(s), true)) previewFailed(s); }   // finished for good: never re-poll it
        else genTimer = setTimeout(() => poll().catch(err => run === genRun && !whileMin(["Something went wrong.", "Tap to see"], () => genFail(err, null), true) && genFail(err, null)), POLL_MS);
      };
      await poll();
    } catch (err) { if (run === genRun && !whileMin(["Something went wrong.", "Tap to see"], () => genFail(err, kind), true)) genFail(err, kind); }
  }
  function finishGen(s) {
    const job = st.pending; st.pending = null;
    const again = st.revealed;
    st.previewId = job.id; st.attempt = job.attempt;
    st.portraitUrl = s.portrait_url || st.portraitUrl;
    st.retriesLeft = s.retries_left != null ? +s.retries_left : job.attempt >= 2 ? 0 : 1;
    closeModal();
    st.revealed = true;
    if (s.book_art) Object.assign(st.art, s.book_art);
    // the second drawing replaces the first everywhere; on the first reveal, the rebuild drops the "Change name" pill (locked now)
    if (s.book_art || again || $('.bk-pill[data-go="child"]', bk)) {
      $(".bk-book", bk).innerHTML = bookHTML(); applyView();
    }
    st.art.cover = s.cover_url || st.art.cover;
    st.coverCartUrl = s.cover_cart_url || null;     // a 90-day signed link to the watermarked cover, for the cart line's picture
    live();
    revealCover(false, again);
  }
  function genFail(err, kind) {
    console.error("[pb] preview failed", err);
    const rateLimited = !!(err && /^rate_limited_(email|device|ip)$/.test(err.code || ""));
    if (err && (err.code === "retry_limit" || rateLimited)) {
      st.pending = null;
      if (kind === "retry" || err.code === "retry_limit") st.retriesLeft = 0;
      if (st.sentPhotos) { st.photos = st.sentPhotos; st.sentPhotos = null; }
      live(); stepLimit(rateLimited ? "previews" : "redo"); return;
    }
    if (err && err.code === "busy") {             // 503 at the daily cap: no preview was made, the redo is not used
      st.pending = null;
      if (kind === "retry" && st.sentPhotos) { st.photos = st.sentPhotos; st.sentPhotos = null; }  // the shown preview's photos
      live(); stepBusy(); return;
    }
    const m = modal(`${head("preview", "Something went wrong, please try again")}
      <div class="bk-mf"><button type="button" class="bk-btn" id="pv-retry">Try again</button></div>`);
    wireCancel(m); $("#pv-retry", m).onclick = () => stepGen(st.pending ? null : kind || "new");
  }
  /* The preview (or its cover, after the pick) ended "failed": it can't be resumed. Forget it, including an earlier
     reveal (a failed retry run has no art to order), keep the photos and the child, and start a NEW preview from the
     photo step: POST /previews, never POST /retry, which would spend the customer's one redo on our failure.
     needs_photo goes straight to the photo step with the server's per-photo verdicts. */
  function previewFailed(s) {
    const wasRevealed = st.revealed;
    Object.assign(st, { pending: null, previewId: null, revealed: false, attempt: 0, retriesLeft: null, portraitUrl: null,
                        retryMode: false, sentPhotos: null, coverCartUrl: null });
    delete st.art.cover;
    if (wasRevealed) { $(".bk-book", bk).innerHTML = bookHTML(); applyView(); }
    live();
    if (s && s.error_code === "no_usable_photo") { st.photoFeedback = s.photo_feedback || []; closeModal(); openStep("photos"); return; }
    failModal();
  }
  function failModal() {
    const m = modal(`${plainHead("Close", "", "Something went wrong, please try again")}
      <div class="bk-mf"><button type="button" class="bk-btn" id="pf-again">Try again</button></div>`);
    wireCancel(m);
    // from the start form (a purged preview link) the form itself is the way back in
    $("#pf-again", m).onclick = () => { if (bk.dataset.screen === "start") closeModal(); else { closeModal(); openStep("photos"); } };
  }
  /* D2: the customer picks between the two drawings; only then is the cover painted. The pick carries the run the
     customer was shown (the status payload's attempt), so a stale tab can't pick for a newer run. */
  function stepPick(s) {
    const job = st.pending, run = genRun, attempt = +(s.attempt || job.attempt || 1), sheets = s.sheets || [];
    const m = modal(`${plainHead("Close", "", "Which one looks more like {n}?", copy("pickIntro", "We drew {n} two ways. Pick the one that looks most like {him}, and we'll paint {him} onto the cover."))}
      <div class="bk-mb"><div class="bk-pick">${sheets.map((x, i) => `<div class="bk-pickopt">
          <button type="button" class="bk-pickzoom" data-zoom="${i}" aria-label="See drawing ${esc(x.sheet)} full screen"><img src="${esc(x.url)}" alt="Drawing ${esc(x.sheet)}"><i class="bk-zoomic" aria-hidden="true">⤢</i></button>
          <button type="button" class="bk-btn bk-pickbtn" data-sheet="${esc(x.sheet)}">This one</button></div>`).join("")}</div>
        <p class="bk-zoomtip">Tap a drawing to see it up close.</p>
        <p class="bk-cmsg" aria-live="polite"></p>
        <p class="bk-fine">${esc(copy("pickNote", "Not sure yet? We'll email you both, so you can choose later."))}</p></div>`, "pick");
    wireCancel(m);
    const opts = $$(".bk-pickbtn", m), msg = $(".bk-cmsg", m);
    const say = (cls, text) => { msg.className = "bk-cmsg" + (cls ? " " + cls : ""); msg.textContent = text; };
    async function pick(sheet) {
      if (opts.some(x => x.disabled)) return;
      opts.forEach(x => (x.disabled = true)); say("", "");
      try {
        await PersonalizeAPI.pick(job.id, sheet, attempt);
        st.pending = { ...job, attempt, picked: sheet };
        if (run === genRun) stepGen(null);                      // the cover is being painted: poll again
      } catch (err) {
        if (run !== genRun) return;                             // the customer closed the modal meanwhile
        if (err && err.code === "busy") {                       // 503 at the daily cap: nothing changed, pick again later
          say("warn", "We're making a lot of previews right now. Please try again in a little while.");
          opts.forEach(x => (x.disabled = false)); return;
        }
        if (err && (err.code === "not_awaiting_pick" || err.code === "already_picked")) {     // 409: reload the status
          say("warn", "This preview has moved on");
          genTimer = setTimeout(() => run === genRun && stepGen(null), 1800); return;
        }
        genFail(err, null);
      }
    }
    opts.forEach(b => (b.onclick = () => pick(+b.dataset.sheet)));
    // a tap on a drawing opens the full-screen viewer; closing it comes back here with nothing picked
    $$(".bk-pickzoom", m).forEach(b => (b.onclick = () => sheetViewer(sheets, +b.dataset.zoom, sheet => { if (run === genRun && m.isConnected) pick(sheet); }, b)));
  }
  /* The two drawings, full screen (Task B): pinch-zoom and pan on touch, wheel/trackpad zoom and drag on desktop,
     double-tap/double-click to zoom, flip between the drawings (arrows, swipe, keys) or see them side by side, with the
     zoom kept in step so the same detail can be compared. Closing picks nothing; "Pick this one" goes through onPick. */
  function sheetViewer(sheets, start, onPick, opener, o = {}) {      // o: { max, title, label } (the cover view: a small zoom, no pick)
    const MAX = o.max || 6, n = sheets.length, lab = i => o.label || `Drawing ${sheets[i].sheet}`;
    let cur = start, both = false, s = 1, tx = 0, ty = 0;      // one zoom for every pane: scale, pan as a share of the pane
    const v = el(`<div class="bk-zv${onPick ? "" : " nopick"}" role="dialog" aria-modal="true" aria-label="${esc(o.title || "Compare the drawings")}">
      <div class="bk-zv-top"><b class="bk-zv-t" aria-live="polite"></b>
        <div class="bk-zv-tools">${n > 1 ? `<button type="button" class="bk-zv-both" aria-pressed="false">Side by side</button>` : ""}
          <button type="button" class="bk-zv-x" aria-label="Close">✕</button></div></div>
      <div class="bk-zv-stage"></div>
      ${n > 1 ? `<button type="button" class="bk-zv-nav prev" aria-label="Previous drawing">‹</button><button type="button" class="bk-zv-nav next" aria-label="Next drawing">›</button>` : ""}
      <div class="bk-zv-bot"><span class="bk-zv-hint"></span><div class="bk-zv-picks"></div></div>
    </div>`);
    const stage = $(".bk-zv-stage", v), title = $(".bk-zv-t", v), picks = $(".bk-zv-picks", v);
    $(".bk-zv-hint", v).textContent = matchMedia("(pointer: coarse)").matches ? "Pinch or double-tap to zoom" : "Scroll or double-click to zoom, drag to look around";
    const shown = () => (both ? sheets.map((_, i) => i) : [cur]);
    const panes = () => $$(".bk-zv-pane", stage);
    function apply() {
      panes().forEach(p => { const im = p.firstElementChild; im.style.transform = `translate(${tx * p.clientWidth}px, ${ty * p.clientHeight}px) scale(${s})`; });
      v.classList.toggle("zoomed", s > 1.01);
    }
    function clampPan() { s = clamp(s, 1, MAX); tx = clamp(tx, 1 - s, 0); ty = clamp(ty, 1 - s, 0); }
    function zoomAt(pane, cx, cy, ns) {      // keep the point under (cx, cy) of `pane` still
      const r = pane.getBoundingClientRect(), px = (cx - r.left) / r.width, py = (cy - r.top) / r.height;
      ns = clamp(ns, 1, MAX);
      tx = px - ((px - tx) * ns) / s; ty = py - ((py - ty) * ns) / s; s = ns;
      clampPan(); apply();
    }
    function paint() {
      v.classList.toggle("both", both);
      stage.innerHTML = shown().map(i => `<div class="bk-zv-pane" data-i="${i}"><img src="${esc(sheets[i].url)}" alt="${esc(lab(i))}" draggable="false">
        ${both ? `<span class="bk-zv-lab">${esc(lab(i))}</span>` : ""}</div>`).join("");
      title.textContent = o.title || (both ? "Both drawings" : `Drawing ${sheets[cur].sheet} of ${n}`);
      picks.innerHTML = !onPick ? "" : shown().map(i => `<button type="button" class="bk-btn bk-zv-pick" data-sheet="${esc(sheets[i].sheet)}">${both ? `Pick drawing ${esc(sheets[i].sheet)}` : "Pick this one"}</button>`).join("");
      const b = $(".bk-zv-both", v); if (b) { b.setAttribute("aria-pressed", both); b.textContent = both ? "One at a time" : "Side by side"; }
      $$(".bk-zv-pick", picks).forEach(b2 => (b2.onclick = () => { const sh = +b2.dataset.sheet; close(); onPick(sh); }));
      $$(".bk-zv-pane img", stage).forEach(im => (im.complete ? apply() : (im.onload = apply)));
      apply();
    }
    const go = d => { if (both || n < 2) return; cur = (cur + d + n) % n; paint(); };

    // pointers: one finger / the mouse pans (when zoomed) or swipes to the other drawing; two fingers pinch
    const pts = new Map();
    let last = null, pinch = null, swipe = null, tap = { t: 0, x: 0, y: 0 };
    stage.addEventListener("pointerdown", e => {
      const pane = e.target.closest(".bk-zv-pane"); if (!pane) return;
      stage.setPointerCapture && stage.setPointerCapture(e.pointerId);
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY, pane });
      if (pts.size === 2) { const [a, b] = [...pts.values()]; pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), s, pane: a.pane }; swipe = null; }
      else { last = { x: e.clientX, y: e.clientY, pane }; swipe = s <= 1.01 ? { x: e.clientX, y: e.clientY, t: Date.now() } : null; }
    });
    stage.addEventListener("pointermove", e => {
      const p = pts.get(e.pointerId); if (!p) return;
      p.x = e.clientX; p.y = e.clientY;
      if (pinch && pts.size >= 2) {
        const [a, b] = [...pts.values()], d = Math.hypot(a.x - b.x, a.y - b.y);
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
        if (pinch.mx != null) { const r = pinch.pane.getBoundingClientRect(); tx += (mx - pinch.mx) / r.width; ty += (my - pinch.my) / r.height; }
        pinch.mx = mx; pinch.my = my;
        zoomAt(pinch.pane, mx, my, (pinch.s * d) / pinch.d);
        return;
      }
      if (last && s > 1.01) {
        const r = last.pane.getBoundingClientRect();
        tx += (e.clientX - last.x) / r.width; ty += (e.clientY - last.y) / r.height; clampPan(); apply();
      }
      last = { x: e.clientX, y: e.clientY, pane: last ? last.pane : p.pane };
    });
    const up = e => {
      const p = pts.get(e.pointerId); if (!p) return;
      pts.delete(e.pointerId);
      if (pts.size < 2) pinch = null;
      if (pts.size === 1) { const [q] = [...pts.values()]; last = { x: q.x, y: q.y, pane: q.pane }; }
      if (pts.size) return;
      last = null;
      if (swipe && e.type === "pointerup") {
        const dx = e.clientX - swipe.x, dy = e.clientY - swipe.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > 1.5 * Math.abs(dy) && Date.now() - swipe.t < 700) { go(dx < 0 ? 1 : -1); swipe = null; return; }
      }
      swipe = null;
      // a double tap (touch/pen; the mouse has dblclick) zooms in at the tap, or back out
      if (e.type === "pointerup" && e.pointerType !== "mouse") {
        const now = Date.now();
        if (now - tap.t < 320 && Math.hypot(e.clientX - tap.x, e.clientY - tap.y) < 30) { dbl(p.pane, e.clientX, e.clientY); tap.t = 0; }
        else tap = { t: now, x: e.clientX, y: e.clientY };
      }
    };
    stage.addEventListener("pointerup", up); stage.addEventListener("pointercancel", up);
    function dbl(pane, x, y) { if (s > 1.01) { s = 1; tx = ty = 0; apply(); } else zoomAt(pane, x, y, Math.min(2.5, MAX)); }
    // (pointer capture retargets click/dblclick to the stage: find the pane under the point)
    const paneAt = (x, y) => panes().find(p => { const r = p.getBoundingClientRect(); return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom; });
    stage.addEventListener("dblclick", e => { const pane = paneAt(e.clientX, e.clientY); if (pane) { e.preventDefault(); dbl(pane, e.clientX, e.clientY); } });
    stage.addEventListener("wheel", e => {
      const pane = e.target.closest(".bk-zv-pane"); if (!pane) return;
      e.preventDefault();
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;      // lines -> px
      zoomAt(pane, e.clientX, e.clientY, s * Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0022)));   // ctrlKey: a trackpad pinch
    }, { passive: false });

    function onKey(e) {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
      else if (e.key === "ArrowRight" || e.key === "ArrowLeft") { e.preventDefault(); e.stopPropagation(); go(e.key === "ArrowRight" ? 1 : -1); }
      else if (e.key === "+" || e.key === "=" || e.key === "-") { e.stopPropagation(); const r = stage.getBoundingClientRect(); zoomAt(panes()[0], r.left + r.width / 2, r.top + r.height / 2, s * (e.key === "-" ? 1 / 1.4 : 1.4)); }
      else if (e.key === "Tab") {                                     // keep focus inside the viewer
        const f = $$("button", v).filter(b => b.offsetParent !== null); if (!f.length) return;
        const i = f.indexOf(document.activeElement);
        if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    }
    function close() {
      removeEventListener("keydown", onKey, true); removeEventListener("resize", apply);
      v.remove(); if (opener && opener.isConnected) opener.focus();
    }
    v.addEventListener("click", e => e.stopPropagation());          // not the builder's own click handling
    $(".bk-zv-x", v).onclick = close;
    const bb = $(".bk-zv-both", v); if (bb) bb.onclick = () => { both = !both; paint(); };
    $$(".bk-zv-nav", v).forEach(b => (b.onclick = () => go(b.classList.contains("next") ? 1 : -1)));
    addEventListener("keydown", onKey, true); addEventListener("resize", apply);
    bk.append(v); paint();
    $(".bk-zv-x", v).focus();
    return { close };
  }
  function startRetry() {
    if (!retryAvailable()) { stepLimit(); return; }
    st.retryMode = true; st.sentPhotos = st.photos.slice();
    stepPhotos();
  }
  function cancelRetry() {
    if (!st.retryMode) return;
    st.retryMode = false;
    if (st.sentPhotos) st.photos = st.sentPhotos;
    st.sentPhotos = null; live();
  }
  /* 503 busy on create or retry (the daily preview-spend cap): later today, not "something went wrong", and no
     "Try again" that would post again at once. */
  function stepBusy() {
    const m = modal(`${plainHead("Close", "", "We're making a lot of previews right now", "Please try again later today. Nothing was used up.")}
      <div class="bk-mf"><button type="button" class="bk-btn" id="bz-ok">Back to the book</button></div>`);
    wireCancel(m); $("#bz-ok", m).onclick = closeModal;
  }
  /* "redo": the preview's one redo is used. "previews": the free-preview limit per email/device/IP (429 on create). */
  function stepLimit(kind = "redo") {
    const P = kind === "previews";
    const m = modal(`${P ? plainHead("Close", "", "You've made the maximum number of free previews for now", "From here, we'll help you ourselves.")
                         : plainHead("Close", "", "We've already redrawn {n} once", "Each preview includes one redo. From here, we'll help you ourselves.")}
      <div class="bk-mb"><p class="bk-quiet big">${P ? "Need another preview?" : "Still not quite right?"} ${mailHTML()} and we'll make it right.</p>
        <p class="bk-reassure">${esc(fill(copy("orderNote", "{N}'s book is made the moment you order and goes straight to print.")))}</p></div>
      <div class="bk-mf"><button type="button" class="bk-btn" id="lm-ok">Back to the book</button></div>`);
    wireCancel(m); $("#lm-ok", m).onclick = closeModal;
  }

  /* ---------------------------------------------------------------- photo tips guide
     Small line drawings only (no photos of real children). */
  function tipIcon(kind, good) {
    const face = `<circle cx="32" cy="34" r="14" fill="#f6d6ba" stroke="#1e2a3f" stroke-width="2"/>
      <path d="M18 31c0-9 6-15 14-15s14 6 14 15c-3-4-8-6-14-6s-11 2-14 6z" fill="#c9773a" stroke="#1e2a3f" stroke-width="2" stroke-linejoin="round"/>
      <circle cx="27" cy="35" r="1.8" fill="#1e2a3f"/><circle cx="37" cy="35" r="1.8" fill="#1e2a3f"/>
      <path d="M27 40.5q5 4 10 0" fill="none" stroke="#1e2a3f" stroke-width="2" stroke-linecap="round"/>`;
    const small = (x, y, s, r = 0) => `<g transform="translate(${x} ${y}) rotate(${r}) scale(${s}) translate(-32 -34)">${face}</g>`;
    const art = {
      frame: `<rect x="9" y="10" width="46" height="46" rx="5" fill="#fff" stroke="#cdbf9f" stroke-width="2"/>${face}`,
      light: `<g stroke="#f0a92b" stroke-width="2.4" stroke-linecap="round"><path d="M51 4v4M51 20v4M41 14h4M57 14h4M44 7l2.5 2.5M55.5 18.5L58 21M44 21l2.5-2.5M55.5 9.5L58 7"/></g><circle cx="51" cy="14" r="4.5" fill="#f5b82e"/>${face}`,
      crop: `<g opacity=".32">${small(56, 40, .75)}</g>${face}
        <path d="M13 22v-8h8M43 14h8v8M51 48v8h-8M21 56h-8v-8" fill="none" stroke="#57ad4e" stroke-width="3" stroke-linecap="round"/>`,
      angles: `<g transform="rotate(-14 20 36)"><rect x="6" y="22" width="24" height="30" rx="3" fill="#fff" stroke="#cdbf9f" stroke-width="1.6"/>${small(18, 36, .55, 0)}</g>
        <g transform="rotate(12 46 36)"><rect x="34" y="22" width="24" height="30" rx="3" fill="#fff" stroke="#cdbf9f" stroke-width="1.6"/>${small(46, 36, .55, 0)}</g>
        <rect x="19" y="16" width="26" height="34" rx="3" fill="#fff" stroke="#cdbf9f" stroke-width="1.8"/>${small(32, 32, .62)}`,
      shades: `${face}<g fill="#1e2a3f"><rect x="21" y="31" width="10" height="7" rx="2.5"/><rect x="33" y="31" width="10" height="7" rx="2.5"/><rect x="30" y="32.5" width="4" height="1.8"/></g>`,
      hat: `${face}<path d="M17 28c1-10 7-15 15-15s14 5 15 15z" fill="#e05a47" stroke="#1e2a3f" stroke-width="2" stroke-linejoin="round"/><path d="M40 27h14" stroke="#1e2a3f" stroke-width="3" stroke-linecap="round"/>`,
      filter: `<ellipse cx="22" cy="14" rx="4.5" ry="10" fill="#f7b3c8" stroke="#1e2a3f" stroke-width="2" transform="rotate(-16 22 14)"/><ellipse cx="42" cy="14" rx="4.5" ry="10" fill="#f7b3c8" stroke="#1e2a3f" stroke-width="2" transform="rotate(16 42 14)"/>
        ${face}<circle cx="32" cy="34" r="14" fill="#b56cf0" opacity=".28"/><path d="M50 40l1.6 3.4 3.4 1.6-3.4 1.6L50 50l-1.6-3.4-3.4-1.6 3.4-1.6zM12 42l1 2 2 1-2 1-1 2-1-2-2-1 2-1z" fill="#b56cf0"/>`,
    }[kind] || face;
    const badge = good ? `<circle cx="53" cy="53" r="8.5" fill="#57ad4e" stroke="#fff" stroke-width="2"/><path d="M49 53l3 3 5.5-6" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>`
                       : `<circle cx="53" cy="53" r="8.5" fill="#e0533f" stroke="#fff" stroke-width="2"/><path d="M49.5 49.5l7 7M56.5 49.5l-7 7" stroke="#fff" stroke-width="2.4" stroke-linecap="round"/>`;
    return `<svg viewBox="0 0 64 64" width="56" height="56" aria-hidden="true" focusable="false">${art}${badge}</svg>`;
  }
  const TIPS_DO = [["frame", "A clear, recent photo"], ["light", "Face toward the camera, in good light"],
                   ["crop", "Crop to just {n}; one child per photo"], ["angles", "2–4 photos from different angles help"]];
  const TIPS_DONT = [["shades", "No sunglasses"], ["hat", "No hats"], ["filter", "No heavy filters"]];
  function openTips(mode) {
    const li = ([ic, t], good) => `<li class="${good ? "do" : "dont"}">${tipIcon(ic, good)}<span>${t}</span></li>`;
    let foot;
    if (mode === "photos") foot = `<div class="bk-mf"><button type="button" class="bk-btn" id="tp-back">Back to photos</button></div>`;
    else if (retryAvailable()) foot = `<div class="bk-mf bk-tipsft"><button type="button" class="bk-btn" id="tp-retry">Try once more with new photos</button>
        <p>You can redo the preview once. ${esc(copy("orderNote", "{N}'s book is made the moment you order and goes straight to print."))}</p>
        <button type="button" class="bk-link" id="tp-keep">Keep this preview</button></div>`;
    else foot = `<div class="bk-mf bk-tipsft"><p class="bk-quiet big">Still not quite right? ${mailHTML()} and we'll make it right.</p>
        <p>${esc(copy("orderNote", "{N}'s book is made the moment you order and goes straight to print."))}</p><button type="button" class="bk-btn ghost" id="tp-keep">Back to the book</button></div>`;
    const m = modal(`${plainHead(mode === "photos" ? "Back" : "Close", "", "Photo tips", "The drawing can only be as good as the photos. What helps most:")}
      <div class="bk-mb"><h3 class="bk-tiph">Do</h3><ul class="bk-tipgrid">${TIPS_DO.map(x => li(x, 1)).join("")}</ul>
        <h3 class="bk-tiph no">Avoid</h3><ul class="bk-tipgrid three">${TIPS_DONT.map(x => li(x, 0)).join("")}</ul>
        <p class="bk-gift">${icon("gift")}Buying as a gift? Ask the family for a clear, recent photo or two.</p></div>
      ${foot}`, "tips");
    const back = goBack(() => stepPhotos());
    wireCancel(m, mode === "photos" ? back : closeModal);
    const b = $("#tp-back", m); if (b) b.onclick = back;
    const r = $("#tp-retry", m); if (r) r.onclick = startRetry;
    const k = $("#tp-keep", m); if (k) k.onclick = closeModal;
  }

  /* "Make it a gift": the dedication and a sign-off line in one optional step (one Skip for both). Both print on the title
     page, which updates live as they type. The sign-off is the buyer's whole line ("With love, Aunt Sarah", "Love, Grandma
     and Grandpa"), printed exactly as typed; the cart property "Sign-off" carries it ("None" when empty).
     focus: the field to start in ("mg-d" or "mg-f"), when the progress modal's "Write a dedication" / "Add who it's from" opened it. */
  function stepGift(focus) {
    const DMAX = 90, FMAX = 40;
    const m = modal(`${head("gift", "Make it a gift", "Optional. Both are printed on the title page, under 'Made especially for {n}'.")}
      <div class="bk-mb"><div class="bk-gprev-slot"></div>
        <label class="bk-f"><span>Dedication</span><textarea id="mg-d" maxlength="${DMAX}" rows="3" placeholder="Add a few words (optional)"></textarea></label>
        <div class="bk-count"><span id="mg-dc"></span> / ${DMAX}</div>
        <label class="bk-f"><span>Sign-off</span><input type="text" id="mg-f" maxlength="${FMAX}" placeholder="Love, Grandma and Grandpa" autocomplete="off" aria-describedby="mg-fh">
          <small class="bk-hint" id="mg-fh">Written just as you type it, e.g. With love, Aunt Sarah</small></label>
        <div class="bk-count"><span id="mg-fc"></span> / ${FMAX}</div></div>
      <div class="bk-mf two"><button type="button" class="bk-btn ghost" id="mg-skip">Skip</button><button type="button" class="bk-btn" id="mg-go">Continue</button></div>`, "gift");
    wireCancel(m);
    // added after modal(), which fills {…} in its HTML once: the preview's data-tpl templates must stay live for live()
    $(".bk-gprev-slot", m).outerHTML = titlePreview();
    const d = $("#mg-d", m), f = $("#mg-f", m), dc = $("#mg-dc", m), fc = $("#mg-fc", m);
    d.value = ded(); f.value = st.from;
    // Skip clears both: once either is saved (editing from the check step), only Continue, so nothing is erased by surprise
    if (st.dedication || st.from) $("#mg-skip", m).style.display = "none";
    const upd = () => {
      st.dedication = d.value.trim(); st.from = f.value.replace(/\s+/g, " ").trim();
      dc.textContent = d.value.length; fc.textContent = f.value.length; live();
    };
    d.oninput = f.oninput = upd; upd();
    $("#mg-skip", m).onclick = () => { d.value = f.value = ""; st.dedication = ""; st.from = ""; st.giftDone = true; advance(); };
    $("#mg-go", m).onclick = () => { upd(); st.giftDone = true; advance(); };
    if (focus) $("#" + focus, m)?.focus({ preventScroll: true });
  }

  /* "Check {N}'s book": what we'll print, before the cart. The child's details come from the preview (read-only, see
     childLocked); only the dedication and sign-off can still change, through the gift step, which comes back here. */
  function stepCheck(_btn) {
    const g = st.gender === "girl" ? "Girl" : "Boy";
    const chip = (label, value, edit) => {
      const inner = `<small>${label}</small><b>${esc(value)}</b>${edit ? icon("pen", 14) : icon("lock", 14)}`;
      return edit ? `<li class="bk-ck ed"><button type="button" data-edit="${edit}" aria-label="${esc(label)}: ${esc(value)}. Change">${inner}</button></li>`
                  : `<li class="bk-ck">${inner}</li>`;
    };
    const m = modal(`${plainHead("Back", "", "Check {N}'s book", copy("checkIntro", "Here's what we'll print."))}
      <div class="bk-mb"><div class="bk-ck-cover">${st.art.cover ? `<img src="${esc(st.art.cover)}" alt="">` : ""}</div>
        <p class="bk-ck-sum">${esc(fill(copy("checkSummary", `A hardcover copy of ${TITLE} for {N}${!st.age ? "" : String(st.age) === "0" ? ", under 1" : `, age ${st.age}`}.`)))}</p>
        <ul class="bk-cks">${chip("Name", st.name)}${chip("Boy or girl", g)}${chip("Age", st.age ? ageLabel(st.age) : "Not set")}${chip("Eye color", st.eyes || "Not set")}
          ${chip("Dedication", (st.dedication || "").trim() || "None", "gift")}${chip("Sign-off", st.from || "None", "gift")}</ul>
        <p class="bk-quiet">${icon("lock")} ${MODEL.copy?.checkDetails ? esc(copy("checkDetails", "")) : `{N}'s details are drawn in. Need a change? ${mailHTML()}`}</p></div>
      <div class="bk-mf"><p class="bk-ckerr" role="alert" hidden>Sorry, we couldn't add that to your cart. Please try again.</p>
        <button type="button" class="bk-btn buy" id="ck-go">Looks right, add to cart</button></div>`, "check");
    wireCancel(m);
    // on the buttons themselves: .bk-m is reused by every step, so a listener on it would outlive this screen
    $$('[data-edit="gift"]', m).forEach(b => (b.onclick = () => { if (!carting) stepGift(); }));
    const go = $("#ck-go", m), err = $(".bk-ckerr", m);
    go.onclick = async () => {
      err.hidden = true;
      // a book not yet on sale shows the real step, and the add only explains itself; it never touches the cart
      if (!ORDERING_OPEN) { err.textContent = MODEL.ordering?.message || "This book isn't on sale yet, so nothing was added to your cart."; err.hidden = false; return; }
      const ok = await addToCart(go, { quiet: true });
      // still this screen (Back or the backdrop may have closed it while the add ran): close on success, explain a failure here
      const here = mw.classList.contains("open") && m.classList.contains("check");
      if (ok) { if (here) closeModal(); }
      else if (here) err.hidden = false;
      else toast("Sorry, we couldn't add that to your cart. Please try again.");
    };
  }

  // ---------------------------------------------------------------- reveals
  function swapArt(id, animate, done) {
    const sh = $(`.bk-sheet[data-id="${id}"]`, bk); if (!sh) return;
    const pd = pageData(id), single = id === "cover", img = art(id);
    const c = onPage(pd, single);
    const pages = $$(".bk-pg:not(.blank)", sh);
    if (!animate || calm()) { pages.forEach(p => (p.style.backgroundImage = `url('${img}')`)); sh.classList.add("real"); done && done(); return; }
    const im = new Image(); im.src = img;
    const go = () => {
      pages.forEach(pg => {
        const s = pg.dataset.side, x = s === "C" ? c.x : s === "L" ? c.x * 2 : (c.x - 50) * 2;
        const layer = el(`<div class="bk-rv" style="background-image:url('${img}')"></div>`);
        pg.insertBefore(layer, pg.firstChild);
        if (layer.animate) layer.animate([{ clipPath: `circle(0% at ${x}% ${c.y}%)` }, { clipPath: `circle(160% at ${x}% ${c.y}%)` }],
          { duration: 2100, easing: "cubic-bezier(.45,.05,.25,1)", fill: "forwards" });
      });
      sh.classList.add("real", "flash");
      setTimeout(() => {
        pages.forEach(p => (p.style.backgroundImage = `url('${img}')`));
        $$(".bk-rv", sh).forEach(n => n.remove()); sh.classList.remove("flash");
        done && done();
      }, 2400);
    };
    im.decode ? im.decode().then(go, go) : go();
  }
  function scrollTo(id, smooth) {
    let sh = $(`.bk-sheet[data-id="${id}"]`, bk); if (!sh) return;
    if (st.view === "one" && PAGES[id] && /^p\d\d-/.test(id)) sh = $(`.bk-pg.${onPage(PAGES[id], false).side}`, sh) || sh;
    const top = sh.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - (phone() ? 12 : 20);
    scroller.scrollTo({ top, behavior: smooth && !calm() ? "smooth" : "instant" });
  }
  function revealCover(instant, again) {
    scrollTo("cover", !instant);
    const msg = again ? `Here's the new drawing of ${st.name}.` : `Here's ${st.name} on the cover!`;
    if (instant) { swapArt("cover", false); toast(msg, true); return; }
    setTimeout(() => swapArt("cover", true, () => toast(msg)), 500);
  }
  let toastT;
  function toast(msg, stay) {
    const t = $(".bk-toast", bk); if (!t) return;
    t.textContent = msg; t.classList.add("on");
    clearTimeout(toastT); if (!stay) toastT = setTimeout(() => t.classList.remove("on"), 4200);
  }

  // ---------------------------------------------------------------- cart (REAL Shopify Ajax cart)
  function cartProperties() {
    return {
      "Child name": st.name,
      "Age": ageLabel(st.age),
      "Boy/Girl": st.gender === "girl" ? "Girl" : "Boy",
      "Eye color": st.eyes,
      "Dedication": (st.dedication || "").trim(),
      "Sign-off": st.from || "None",
      "_preview_id": st.previewId || "",
      "_preview_attempt": String(st.attempt || 1),
      // Ped. Order Management waits for the press PDFs at customized-press-files/personalized/<this id>/;
      // it has to be set here, since line item properties can't be added once the order is placed. Left out,
      // never sent blank, when there is no preview (the order app reads blank as absent anyway).
      ...(st.previewId ? { "_personalized-file-id": st.previewId } : {}),
      ...(st.coverCartUrl ? { "_cover_image": st.coverCartUrl } : {}),
    };
  }
  async function cartAdd(id) {
    const r = await fetch("/cart/add.js", {
      method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1, properties: cartProperties() }] }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.description || data.message || "cart add failed (" + r.status + ")");
    return (data.items || [data])[0];
  }
  // One preview is ONE cart line (owner and Chris, 2026-09-30): the press files are keyed by the preview, so two lines
  // of one preview with different text would print one book's PDFs for both. Adding the preview again with a changed
  // dedication, sign-off or attempt edits its line in place (quantity kept); adding it unchanged adds a copy
  // (Shopify merges identical lines: quantity + 1). More copies are the quantity, all printed from the same PDFs.
  const ofPreview = (i) => ((i && i.properties) || {})._preview_id === st.previewId;
  // _cover_image is a signed link re-issued with every status call (a new expiry each time), so it says nothing about
  // whether the book changed: leave it out, or an unchanged re-add after a reload would look like an edit.
  const VOLATILE = new Set(["_cover_image"]);
  function sameProps(a, b) {
    const norm = (o) => Object.entries(o || {}).filter(([k, v]) => !VOLATILE.has(k) && v != null && String(v) !== "")
      .map(([k, v]) => k + "\u0000" + String(v)).sort().join("\u0001");
    return norm(a) === norm(b);
  }
  async function cartChange(body) {
    const r = await fetch("/cart/change.js", {
      method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.description || data.message || "cart change failed (" + r.status + ")");
    return data;
  }
  async function cartPut(id) {
    if (!st.previewId) return { item: await cartAdd(id), updated: false };
    const cart = await (await fetch("/cart.js", { headers: { Accept: "application/json" } })).json();
    const lines = (cart.items || []).filter(ofPreview);
    const props = cartProperties();
    if (!lines.length) return { item: await cartAdd(id), updated: false };
    if (lines.length === 1 && sameProps(lines[0].properties, props)) {
      // The same book: one more copy on its line. Not /cart/add.js: a re-issued _cover_image would make Shopify
      // start a second line instead of merging.
      const [line] = lines, after = await cartChange({ id: line.key, quantity: (line.quantity || 0) + 1 });
      return { item: (after.items || []).find((i) => i.key === line.key) || line, updated: false };
    }
    // Changed text: edit the line in place. A cart made before this rule may hold several lines of the preview:
    // they fold into the first, keeping every copy. The kept line is changed FIRST (to the new text and the total
    // quantity), so a failure can never leave the customer with fewer copies; only then are the other lines removed,
    // by the keys the cart reports after that change.
    const [line] = lines;
    const quantity = lines.reduce((n, l) => n + (l.quantity || 0), 0);
    const isKept = (i) => ofPreview(i) && sameProps(i.properties, props);
    let after = await cartChange({ id: line.key, quantity, properties: props });
    for (const x of (after.items || []).filter((i) => ofPreview(i) && !isKept(i))) after = await cartChange({ id: x.key, quantity: 0 });
    let kept = (after.items || []).find(isKept);
    // Shopify may merge an extra that already had the new text into the kept line: put the total back
    if (kept && kept.quantity !== quantity) { after = await cartChange({ id: kept.key, quantity }); kept = (after.items || []).find(isKept) || kept; }
    return { item: kept || line, updated: true };
  }
  let carting = false;     // an add to cart is in flight
  /* One add at a time: the button pressed and the bar's Add button are both busy while it runs. Resolves true once the
     line is in the cart (the drawer is open), false on failure (a toast unless quiet: the caller explains it). */
  async function addToCart(btn, { quiet = false } = {}) {
    if (!ORDERING_OPEN) { stepCheck(btn); return false; }
    if (carting) return false;
    carting = true;
    const btns = [...new Set([btn, bar && $(".bk-cont", bar)].filter(Boolean))], labels = btns.map(b => b.innerHTML);
    btns.forEach(b => { b.dataset.busy = "1"; b.disabled = true; b.innerHTML = `<i class="bk-spin sm light"></i> Adding…`; });
    try {
      // The personalized product's variant when the storefront can sell it (checked server-side in the section).
      // Only the demo may fall back to this page's own product as a labelled stand-in: a real preview must never
      // go to checkout as the classic book (it would print the generic edition).
      let item, standIn = false, updated = false;
      // the cart's cover picture: the status only has it once the cover exists, so ask again if the reveal came without it
      if (!st.coverCartUrl && st.previewId) {
        try { const s = await PersonalizeAPI.getStatus(st.previewId); st.coverCartUrl = (s && s.cover_cart_url) || null; } catch (e) { /* the line goes without a picture */ }
      }
      if (CFG.personalizedVariantId) {
        try { ({ item, updated } = await cartPut(CFG.personalizedVariantId)); }
        catch (err) {
          if (!PersonalizeAPI.demo) throw err;
          console.warn("[pb] personalized variant not purchasable, using stand-in", err);
        }
      }
      if (!item) {
        if (!PersonalizeAPI.demo) throw new Error("the personalized edition is not on sale");
        ({ item, updated } = await cartPut(CFG.variantId)); standIn = true;
      }
      let count = null;
      try { const c = await (await fetch("/cart.js", { headers: { Accept: "application/json" } })).json(); count = c.item_count; } catch (e) {}
      if (count != null) { const cc = document.getElementById("cartCount"); if (cc) cc.textContent = count; const tg = document.getElementById("cartToggle"); if (tg) tg.classList.toggle("empty", !count); }
      openCart(item, standIn, updated);
      return true;
    } catch (err) {
      console.error("[pb] add to cart failed", err);
      if (!quiet) toast("Sorry, we couldn't add that to your cart. Please try again.");
      return false;
    } finally {
      carting = false;
      btns.forEach((b, i) => { delete b.dataset.busy; b.disabled = false; b.innerHTML = labels[i]; });
      renderBar();
    }
  }

  let drawer;
  function openCart(item, standIn, updated) {
    if (!drawer) { drawer = el(`<aside id="bkd" class="bk-drawer" aria-label="Cart"></aside>`); document.body.append(drawer); }
    const props = Object.entries(item.properties || {}).filter(([k]) => k[0] !== "_");
    const line = (item.final_line_price ?? item.line_price ?? item.price ?? 0) / 100;
    drawer.innerHTML = `<div class="hd"><b>Your cart</b><button type="button" class="bk-x dark" data-dclose aria-label="Close">✕</button></div>
      <div class="ok">${updated ? "✓ Updated in your cart" : "✓ Added to your cart"}</div>
      ${standIn && CFG.cartNote ? `<div class="demo">${esc(CFG.cartNote.replace(/\{PRICE\}/g, money2(line)))}</div>` : ""}
      <div class="item"><div class="thumb" style="background-image:url('${st.art.cover || U("ph-cover")}')"></div>
        <div class="info"><b>${esc(fill(TITLE))}: Personalized Edition</b><div class="var">${esc(CFG.format || "")}</div>
          <dl>${props.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`).join("")}</dl>
          ${standIn ? `<div class="cartline">In cart as: ${esc(item.product_title || "")} · ${money2(line)}</div>` : ""}</div>
        <div class="pr"><b>${money2(standIn ? PRICE : line)}</b></div></div>
      <div class="ft"><p>${esc(fill(CFG.cartAfter || ""))}</p>
        <a class="bk-btn" href="/cart">View cart</a><button type="button" class="bk-link center" data-dclose>Keep editing</button></div>`;
    drawer.classList.add("open"); document.documentElement.classList.add("bk-dim");
    $$("[data-dclose]", drawer).forEach(b => (b.onclick = closeCart));
  }
  function closeCart() { if (drawer) drawer.classList.remove("open"); document.documentElement.classList.remove("bk-dim"); }

  // ---------------------------------------------------------------- landing (server-rendered; JS only wires the gallery + CTAs)
  function landing() {
    document.addEventListener("click", e => { if (e.target.closest("[data-personalize]")) { e.preventDefault(); openBuilder("start"); } });
    // the curated reviews carousel (bottom of the landing): a scroll-snap row; dots page through it
    $$("[data-pb-carousel]").forEach(track => {
      const dots = track.parentElement.querySelector(".lp-cq-dots"); if (!dots) return;
      const pages = () => Math.max(1, Math.ceil((track.scrollWidth - 4) / track.clientWidth));
      const paint = () => {
        const n = pages(), cur = Math.min(n - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
        if (dots.childElementCount !== (n > 1 ? n : 0)) {
          dots.innerHTML = n > 1 ? Array.from({ length: n }, (_, i) => `<button type="button" tabindex="-1" data-i="${i}"></button>`).join("") : "";
        }
        $$("button", dots).forEach((b, i) => b.classList.toggle("on", i === cur));
      };
      dots.onclick = e => { const b = e.target.closest("button"); if (b) track.scrollTo({ left: +b.dataset.i * track.clientWidth, behavior: smooth() }); };
      track.addEventListener("scroll", () => requestAnimationFrame(paint), { passive: true });
      addEventListener("resize", paint); paint();
    });
  }

  // ---------------------------------------------------------------- debug/screenshot hooks (#s=state, demo only)
  async function samplePhotos() {      // the sample photos, cropped to their stored suggestions without the crop screen
    const out = [];
    for (const u of SAMPLE) {
      const p = await preparePhoto(await fetchBlob(u)); await p.checking;
      out.push(await applyCrop(p, validBox(p.check.suggested_crop) || centeredBox(p.w, p.h)));
    }
    return out;
  }
  async function demo(state) {
    const q = new URLSearchParams(location.hash.slice(1));
    if (q.get("v")) st.view = q.get("v");
    const order = ["start", "preview", "child", "photos", "crop", "generating", "draw", "pick", "revealed", "tips", "retry", "limit", "gift", "ready"];
    const k = order.indexOf(state), at = s => k >= order.indexOf(s);
    if (k < 0) return;
    Object.assign(st, { name: "Calvin", gender: "boy", age: "3", eyes: "Blue", email: "parent@example.com", consent: true });
    if (q.get("name")) st.name = q.get("name").slice(0, MODEL.maxName || 14);            // demo screenshots: #s=preview&name=Sam&g=girl
    if (q.get("g") === "girl" || q.get("g") === "boy") st.gender = q.get("g");
    if (q.get("ded")) st.dedication = q.get("ded");
    if (q.get("from")) st.from = q.get("from");
    if (q.get("consent") === "0") st.consent = false;
    if (q.get("blank")) Object.assign(st, { name: "", age: "", eyes: "", email: "" });   // demo screenshots: an empty child form                   // demo screenshots: the photo step before the box is ticked
    if (state === "start") { openBuilder("start"); return; }
    st.childDone = true;
    if (at("photos") && state !== "crop" && q.get("photos") !== "0") { st.photos = await samplePhotos(); st.sentSig = sig(); }
    if (at("revealed") && DEMO_BOOK.compose) Object.assign(st.art, await demoBookArt(st.gender));
    if (at("revealed")) { st.revealed = true; st.attempt = 1; st.retriesLeft = 1; st.previewId = "demo-" + uuid(); st.portraitUrl = U("boaz-portrait"); st.art.cover = st.art.cover || U("boaz-cover"); }
    if (state === "limit") { st.attempt = 2; st.retriesLeft = 0; }
    if (at("ready")) { st.from = "With love, Aunt Sarah"; st.giftDone = true; }
    openBuilder("preview");
    const after = {
      child: () => stepChild(),
      photos: () => stepPhotos(),
      crop: async () => { stepPhotos(); addBlobs([{ blob: await fetchBlob(SAMPLE[3]) }]); },
      generating: () => stepPreview("gen", 58),
      draw: () => stepGen("new"),            // a real (demo) generation from the start: sheets, then pick, then the cover
      pick: async () => {         // a real demo job whose drawings are done, so picking runs the cover and the reveal
        const job = await PersonalizeAPI.createPreview({ child: childPayload(), photos: st.photos.map(p => p.blob) });
        PersonalizeAPI._jobs[job.preview_id].t0 = 0;
        st.pending = { id: job.preview_id, attempt: 1 };
        stepPick(await PersonalizeAPI.getStatus(job.preview_id));
      },
      revealed: () => revealCover(true),
      tips: () => { revealCover(true); openTips("reveal"); },
      retry: () => { revealCover(true); startRetry(); },
      limit: () => revealCover(true),
      gift: () => { scrollTo("p01"); stepGift(); },
      ready: () => scrollTo("p01"),
    }[state];
    live();
    after && requestAnimationFrame(() => requestAnimationFrame(after));
  }

  /* Shopify's theme-preview bar (#PBarNextFrameWrapper, only on unpublished-theme previews) sits fixed at the
     bottom and would cover the step bar / sticky CTA. Track its height in --pb-pbar so our fixed UI sits above it. */
  function previewBarOffset() {
    let ro;
    const set = () => {
      const w = document.getElementById("PBarNextFrameWrapper");
      let h = 0;
      if (w && getComputedStyle(w).display !== "none") { const r = w.getBoundingClientRect(); h = r.height > 0 ? Math.max(0, Math.min(140, innerHeight - r.top)) : 0; }
      document.documentElement.style.setProperty("--pb-pbar", h + "px");
      if (w && !ro && window.ResizeObserver) { ro = new ResizeObserver(set); ro.observe(w); }
    };
    set(); [500, 1500, 3500, 7000].forEach(t => setTimeout(set, t)); addEventListener("resize", set);
  }

  function init() {
    landing();
    // the title page's name fit and sign-off line are measured in the book's fonts: measure again once they have loaded
    const fitBookText = () => { if (bk) { if (LAYOUT.cover.fit) fitCover(); fitTitle(); } };
    if (document.fonts) { document.fonts.addEventListener && document.fonts.addEventListener("loadingdone", fitBookText); document.fonts.ready.then(fitBookText); }
    addEventListener("resize", fitBookText);
    // while the builder is open, a file dropped just outside the upload area must not make the browser open it
    ["dragover", "drop"].forEach(t => addEventListener(t, e => {
      if (!bk || !bk.classList.contains("open") || !hasFiles(e) || e.defaultPrevented) return;
      e.preventDefault(); e.dataTransfer.dropEffect = "none";
    }));
    previewBarOffset();
    const m = PersonalizeAPI.demo && location.hash.match(/s=([\w-]+)/);
    if (m) demo(m[1]).catch(err => console.error("[pb] demo hook", err));
    const pv = new URLSearchParams(location.search).get("preview");
    if (pv && !PersonalizeAPI.demo) resume(pv);
    addEventListener("keydown", e => {
      if (e.key !== "Escape") return;
      if (drawer && drawer.classList.contains("open")) closeCart();
      else if (mw && mw.classList.contains("open")) { if (genOpen()) minimizeGen(); else closeModal(); }
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init); else init();
})();
